import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Checkbox,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  Autocomplete,
  Collapse,
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SendIcon from '@mui/icons-material/Send';
import UploadIcon from '@mui/icons-material/Upload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RemoveIcon from '@mui/icons-material/Remove';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import API_CONFIG from '../config/api';
import { getToken, apiFetch, getAuthenticatedApiUrl } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { withSetorScope } from '../utils/dataIsolation';
import NotasFiscaisLayout from '../components/notasFiscais/NotasFiscaisLayout';
import ProcDePagamentoView from '../components/notasFiscais/ProcDePagamentoView';
import RelatorioMensalView from '../components/notasFiscais/RelatorioMensalView';
import DashboardFinanceiroPage from './DashboardFinanceiroPage';
import ProcessoPagamentoPage from './ProcessoPagamentoPage';
import NotasFiscaisFluxoView from '../components/notasFiscais/NotasFiscaisFluxoView';
import { isUsuarioSetorContratos } from '../utils/contratosDashboardUtils';

const BACKEND_URL = API_CONFIG.BACKEND_URL;

const withUnidade = (path, unidade) => {
  if (!unidade?.codigo) return path;
  return path.includes('?')
    ? `${path}&unidade=${encodeURIComponent(unidade.codigo)}`
    : `${path}?unidade=${encodeURIComponent(unidade.codigo)}`;
};

export default function NotasFiscaisPage() {
  // Auth context
  const { user, isAdmin, isFinanceiro, isFiscal, fiscalSetor, canEditNota, canMarcarComoPaga, canEnviarContabilidade, isFiscalEspecial, canAddAvaliacao, unidadeSelecionada, canAuthorize, rbacRole } = useAuth();
  const visaoFiscal = isFiscal && !isFinanceiro && !isFiscalEspecial;
  const podeCancelarProcessoNf = isUsuarioSetorContratos({ user, isAdmin, isFiscal });
  const scopeOpts = { user, rbacRole, fiscalSetor };
  // Estados
  const [contratos, setContratos] = useState([]);
  const [contratoSelecionado, setContratoSelecionado] = useState(null);
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [notasFiscaisFiltradas, setNotasFiscaisFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Estados para gráfico por contrato
  const [modalGraficoContrato, setModalGraficoContrato] = useState(false);
  const [dadosGraficoContrato, setDadosGraficoContrato] = useState([]);
  const [loadingGraficoContrato, setLoadingGraficoContrato] = useState(false);
  
  // Estado para expansão do box de notas fiscais
  const [notasExpanded, setNotasExpanded] = useState(false);

  // Form state para adicionar/editar nota fiscal
  const [notaForm, setNotaForm] = useState({
    numeroNotaFiscal: '',
    dataEmissao: '',
    dataRecebimento: '',
    dataEnvioContabilidade: '',
    dataRecebimentoContabilidade: '',
    dataVencimento: '',
    valorBruto: '',
    valorLiquido: '',
    competencia: '',
    anoCompetencia: String(new Date().getFullYear()),
    eventual: false,
    pagamentoEfetuado: false
  });

  // Estado para upload de PDF
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pdfAnexado, setPdfAnexado] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Anos disponíveis para competência (nota fiscal)
  const anosCompetenciaDisponiveis = [2024, 2025, 2026];

  // Lista de meses para competência (por ano selecionado)
  const mesesCompetencia = React.useMemo(() => {
    const ano = parseInt(notaForm.anoCompetencia, 10) || new Date().getFullYear();
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return meses.map(mes => `${mes}/${ano}`);
  }, [notaForm.anoCompetencia]);

  // Estados para documentos adicionais
  const [documentosModalOpen, setDocumentosModalOpen] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [notaSelecionada, setNotaSelecionada] = useState(null);
  const [uploadingDocumento, setUploadingDocumento] = useState(false);
  
  
  // Estados para comprovante de pagamento
  const [modalComprovanteOpen, setModalComprovanteOpen] = useState(false);
  const [notaParaComprovante, setNotaParaComprovante] = useState(null);
  const [comprovanteFile, setComprovanteFile] = useState(null);
  
  // Estados para avaliação mensal
  const [modalAvaliacaoOpen, setModalAvaliacaoOpen] = useState(false);
  const [notaParaAvaliar, setNotaParaAvaliar] = useState(null);
  const [avaliacaoForm, setAvaliacaoForm] = useState({
    criterio1: 3,
    criterio2: 3,
    criterio3: 3,
    criterio4: 3,
    criterio5: 3,
    criterio6: 3,
    criterio7: 3,
    criterio8: 3,
    pontosPositivos: '',
    pontosNegativos: '',
    dataAvaliacao: new Date().toISOString().split('T')[0]
  });
  
  const [tiposDocumentos] = useState({
    atestos: [],
    relatorios: [],
    certidoes: [
      { tipo: 'Federal', subtipo: null },
      { tipo: 'Estadual', subtipo: null },
      { tipo: 'Municipal', subtipo: null },
      { tipo: 'FGTS', subtipo: null },
      { tipo: 'Trabalhista', subtipo: null },
      { tipo: 'Cartão CNPJ', subtipo: null }
    ],
    guias: [
      { tipo: 'INSS', subtipo: 'Guia' },
      { tipo: 'INSS', subtipo: 'Comprovante' },
      { tipo: 'FGTS', subtipo: 'Guia' },
      { tipo: 'FGTS', subtipo: 'Comprovante' }
    ]
  });

  // Estados para edição de informações do contrato
  const [modalEditarContratoOpen, setModalEditarContratoOpen] = useState(false);
  const [contratoEditForm, setContratoEditForm] = useState({
    codigoServico: '',
    codigoFornecedor: ''
  });

  // Modal Detalhes do Contrato (Contrato + abas por aditivo)
  const [modalDetalhesOpen, setModalDetalhesOpen] = useState(false);
  const [detalhesContrato, setDetalhesContrato] = useState(null); // { contrato, aditivos: [{ ...ad, itens: [] }] }
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [tabDetalhes, setTabDetalhes] = useState(0);

  // Filtro de ano para lista de notas fiscais (2025 / 2026)
  const [anoFiltroNotas, setAnoFiltroNotas] = useState('todos');

  // Mapa mês (pt-BR) -> número para ordenar competências Janeiro/2025 a Dezembro/2025
  const ordemMeses = React.useMemo(() => ({
    Janeiro: 1, Fevereiro: 2, Março: 3, Abril: 4, Maio: 5, Junho: 6,
    Julho: 7, Agosto: 8, Setembro: 9, Outubro: 10, Novembro: 11, Dezembro: 12
  }), []);

  // Notas filtradas por ano de competência, ordenadas por competência (Janeiro a Dezembro)
  const notasParaExibir = React.useMemo(() => {
    let list = anoFiltroNotas === 'todos'
      ? [...notasFiscaisFiltradas]
      : notasFiscaisFiltradas.filter((n) => {
          const ano = (n.competencia || '').split('/')[1];
          return ano === anoFiltroNotas;
        });
    list.sort((a, b) => {
      const [mesA, anoA] = (a.competencia || '').split('/');
      const [mesB, anoB] = (b.competencia || '').split('/');
      const numA = (parseInt(anoA, 10) || 0) * 12 + (ordemMeses[mesA] || 0);
      const numB = (parseInt(anoB, 10) || 0) * 12 + (ordemMeses[mesB] || 0);
      return numA - numB;
    });
    return list;
  }, [notasFiscaisFiltradas, anoFiltroNotas, ordemMeses]);

  // Estados para modal de status
  const [modalStatusOpen, setModalStatusOpen] = useState(false);
  const [statusFiltro, setStatusFiltro] = useState('todas');
  const [notasStatus, setNotasStatus] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Estados para menu lateral Notas Fiscais
  const [menuSelected, setMenuSelected] = useState('proc-pagamento');
  const [notasFiscaisRelatorio, setNotasFiscaisRelatorio] = useState([]);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);

  // Importação em lote de notas fiscais
  const [importingNotasExcel, setImportingNotasExcel] = useState(false);
  const [importResultModal, setImportResultModal] = useState({ open: false, result: null });

  // Detalhe do processo (abrir ao clicar no processo)
  const [processoDetalheOpen, setProcessoDetalheOpen] = useState(false);
  const [processoSelecionado, setProcessoSelecionado] = useState(null);
  const [notasDoProcesso, setNotasDoProcesso] = useState([]);
  const [certidoesProcesso, setCertidoesProcesso] = useState([]);
  const [loadingProcessoDetalhe, setLoadingProcessoDetalhe] = useState(false);
  const [editandoCertidaoVencimento, setEditandoCertidaoVencimento] = useState(null);
  const [certidaoVencimentoValor, setCertidaoVencimentoValor] = useState('');
  const [pdfConsolidadoBlobUrl, setPdfConsolidadoBlobUrl] = useState(null);
  const [loadingPdfConsolidado, setLoadingPdfConsolidado] = useState(false);
  const [pdfConsolidadoErroAuth, setPdfConsolidadoErroAuth] = useState(false);
  const [atividadeProcessoStatus, setAtividadeProcessoStatus] = useState(null);

  // Notas do processo ordenadas por competência (Janeiro a Dezembro) para o drawer
  const notasDoProcessoOrdenadas = React.useMemo(() => {
    const list = [...notasDoProcesso];
    list.sort((a, b) => {
      const [mesA, anoA] = (a.competencia || '').split('/');
      const [mesB, anoB] = (b.competencia || '').split('/');
      const numA = (parseInt(anoA, 10) || 0) * 12 + (ordemMeses[mesA] || 0);
      const numB = (parseInt(anoB, 10) || 0) * 12 + (ordemMeses[mesB] || 0);
      return numA - numB;
    });
    return list;
  }, [notasDoProcesso, ordemMeses]);

  // Função para buscar notas por status no modal (com unidade para filtrar por tenant)
  const fetchNotasStatus = async (status) => {
    try {
      setLoadingStatus(true);
      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const qs = params.toString();
      const url = qs ? `${BACKEND_URL}/api/notas-fiscais/status/${status}?${qs}` : `${BACKEND_URL}/api/notas-fiscais/status/${status}`;
      const res = await apiFetch(url);
      if (!res.ok) throw new Error('Erro ao buscar notas por status');
      const data = await res.json();
      
      // Filtrar por setor APENAS se for fiscal comum (financeiro e fiscais especiais vêem tudo)
      let notasFiltradas = data;
      if (isFiscal && !isFinanceiro && !isFiscalEspecial && fiscalSetor) {
        const contratosDoSetor = contratos.filter(contrato => contrato.setor === fiscalSetor);
        const numerosContratosDoSetor = contratosDoSetor.map(c => c.numeroContrato);
        notasFiltradas = data.filter(nota => 
          numerosContratosDoSetor.includes(nota.numero_contrato)
        );
      }
      
      setNotasStatus(notasFiltradas);
    } catch (error) {
      console.error('Erro ao buscar notas por status:', error);
      setNotasStatus([]);
    } finally {
      setLoadingStatus(false);
    }
  };

  // Função para aplicar filtro no modal
  const handleFiltroStatusModal = (status) => {
    setStatusFiltro(status);
    if (status !== 'todas') {
      fetchNotasStatus(status);
    } else {
      setNotasStatus([]);
    }
  };

  // Ao abrir o modal ou trocar de unidade, recarregar notas do status atual (para refletir a unidade selecionada)
  useEffect(() => {
    if (modalStatusOpen && statusFiltro !== 'todas' && unidadeSelecionada?.codigo) {
      fetchNotasStatus(statusFiltro);
    }
  }, [modalStatusOpen, unidadeSelecionada?.codigo]);

  // Função para abrir modal de edição do contrato
  const handleEditarContrato = () => {
    if (contratoSelecionado) {
      setContratoEditForm({
        codigoServico: contratoSelecionado.codigoServico || '',
        codigoFornecedor: contratoSelecionado.codigoFornecedor || ''
      });
      setModalEditarContratoOpen(true);
    }
  };

  // Função para salvar informações do contrato
  const handleSalvarContrato = async () => {
    try {
      const response = await apiFetch(withUnidade(`/api/contratos/${contratoSelecionado.id}/informacoes`, unidadeSelecionada), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contratoEditForm)
      });

      if (response.ok) {
        // Atualizar o contrato selecionado com as novas informações
        setContratoSelecionado({
          ...contratoSelecionado,
          codigoServico: contratoEditForm.codigoServico,
          codigoFornecedor: contratoEditForm.codigoFornecedor
        });
        
        setSnackbar({
          open: true,
          message: 'Informações do contrato atualizadas com sucesso!',
          severity: 'success'
        });
        setModalEditarContratoOpen(false);
      } else {
        throw new Error('Erro ao salvar informações');
      }
    } catch (error) {
      console.error('Erro ao salvar contrato:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar informações do contrato',
        severity: 'error'
      });
    }
  };

  // Função para buscar dados do gráfico por contrato
  const fetchDadosGraficoContrato = async (numeroContrato) => {
    try {
      setLoadingGraficoContrato(true);
      console.log('📊 Buscando dados do gráfico para contrato:', numeroContrato);
      
      const response = await apiFetch(withUnidade(`/api/dashboard/grafico-contrato/${encodeURIComponent(numeroContrato)}`, unidadeSelecionada));
      
      if (!response.ok) {
        throw new Error('Erro ao buscar dados do gráfico');
      }
      
      const data = await response.json();
      console.log('📊 Dados do gráfico recebidos:', data);
      
      setDadosGraficoContrato(data.dados || []);
      
    } catch (error) {
      console.error('❌ Erro ao buscar dados do gráfico:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados do gráfico',
        severity: 'error'
      });
      setDadosGraficoContrato([]);
    } finally {
      setLoadingGraficoContrato(false);
    }
  };

  // Função para abrir modal do gráfico
  const handleAbrirGraficoContrato = () => {
    if (contratoSelecionado) {
      setModalGraficoContrato(true);
      fetchDadosGraficoContrato(contratoSelecionado.numeroContrato);
    }
  };

  // Buscar detalhes do contrato + aditivos (para modal com abas)
  const fetchDetalhesContrato = async () => {
    if (!contratoSelecionado) return;
    setLoadingDetalhes(true);
    setDetalhesContrato(null);
    setTabDetalhes(0);
    try {
      const numeroContrato = contratoSelecionado.numeroContrato;
      const resContrato = await apiFetch(withUnidade(`/api/prestadores?numeroContrato=${encodeURIComponent(numeroContrato)}`, unidadeSelecionada));
      let contrato = contratoSelecionado;
      if (resContrato.ok) {
        const data = await resContrato.json();
        if (data.length > 0) contrato = data[0];
      }
      const resAditivos = await apiFetch(withUnidade(`/api/aditivos?numeroContrato=${encodeURIComponent(numeroContrato)}`, unidadeSelecionada));
      const aditivos = resAditivos.ok ? await resAditivos.json() : [];
      const aditivosComItens = await Promise.all(
        (aditivos || []).map(async (ad) => {
          const resItens = await apiFetch(withUnidade(`/api/aditivos/${ad.id}/itens`, unidadeSelecionada));
          const itens = resItens.ok ? await resItens.json() : [];
          return { ...ad, itens: itens || [] };
        })
      );
      setDetalhesContrato({ contrato, aditivos: aditivosComItens });
    } catch (e) {
      console.error('Erro ao buscar detalhes do contrato:', e);
      setSnackbar({ open: true, message: 'Erro ao carregar detalhes do contrato', severity: 'error' });
    } finally {
      setLoadingDetalhes(false);
    }
  };

  const handleAbrirDetalhes = () => {
    if (contratoSelecionado) {
      setModalDetalhesOpen(true);
      fetchDetalhesContrato();
    }
  };

  const calcularParcelas = (vigenciaInicio, assinatura, parcelas) => {
    if (!vigenciaInicio || !assinatura || !parcelas) return null;
    try {
      const parseData = (str) => {
        const [d, m, a] = (str || '').split('/');
        if (!a) return null;
        return new Date(parseInt(a), parseInt(m, 10) - 1, parseInt(d, 10));
      };
      const dataVigencia = parseData(vigenciaInicio);
      const dataAssinatura = parseData(assinatura);
      if (!dataVigencia || !dataAssinatura) return null;
      const primeiraParcela = new Date(dataVigencia);
      primeiraParcela.setMonth(primeiraParcela.getMonth() + 1);
      primeiraParcela.setDate(1);
      const hoje = new Date();
      const mesesDesdeVigencia = (hoje.getFullYear() - primeiraParcela.getFullYear()) * 12 + (hoje.getMonth() - primeiraParcela.getMonth());
      const parcelaAtual = Math.min(Math.max(1, mesesDesdeVigencia + 1), parseInt(parcelas, 10) || 12);
      const sessentaMeses = new Date(dataAssinatura);
      sessentaMeses.setMonth(sessentaMeses.getMonth() + 60);
      return {
        dataAssinatura: assinatura,
        dataVigencia: vigenciaInicio,
        primeiraParcela: primeiraParcela.toLocaleDateString('pt-BR'),
        parcelaAtual,
        totalParcelas: parseInt(parcelas, 10) || 12,
        sessentaMeses: sessentaMeses.toLocaleDateString('pt-BR')
      };
    } catch (err) {
      return null;
    }
  };

  // Buscar contratos (dados dos prestadores)
  const fetchContratos = useCallback(async () => {
    setLoading(true);
    try {
      // TODO Backend: GET /api/prestadores deve filtrar por ?setor= (Fase 5)
      let url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/prestadores?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/prestadores`;
      if (visaoFiscal && fiscalSetor) {
        url += `${url.includes('?') ? '&' : '?'}setor=${encodeURIComponent(fiscalSetor)}`;
      }
      
      const res = await apiFetch(url);
      if (!res.ok) throw new Error('Erro ao buscar contratos');
      const data = await res.json();
      
      // Reforço client-side (defesa em profundidade)
      let contratosFiltrados = data;
      if (visaoFiscal && fiscalSetor) {
        contratosFiltrados = data.filter(contrato => contrato.setor === fiscalSetor);
      }
      
      setContratos(contratosFiltrados);
    } catch (error) {
      console.error('Erro ao buscar contratos:', error);
      setContratos([]);
    } finally {
      setLoading(false);
    }
  }, [visaoFiscal, fiscalSetor, unidadeSelecionada]);

  // Buscar notas fiscais de um contrato
  const fetchNotasFiscais = async (numeroContrato) => {
    if (!numeroContrato) return;
    
    try {
      const res = await apiFetch(withUnidade(`/api/notas-fiscais/${encodeURIComponent(numeroContrato)}`, unidadeSelecionada));
      if (!res.ok) throw new Error('Erro ao buscar notas fiscais');
      const data = await res.json();
      setNotasFiscais(data);
    } catch (error) {
      console.error('Erro ao buscar notas fiscais:', error);
      setNotasFiscais([]);
    }
  };

  // Buscar TODAS as notas fiscais (para filtros diretos)
  const fetchTodasNotasFiscais = async () => {
    try {
      setLoading(true);
      // Adicionar filtro de unidade na URL
      const url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/notas-fiscais?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/notas-fiscais`;
      
      const res = await apiFetch(url);
      if (!res.ok) throw new Error('Erro ao buscar todas as notas fiscais');
      const data = await res.json();
      
      // Filtrar por setor APENAS se for fiscal comum (financeiro e fiscais especiais vêem tudo)
      let notasFiltradas = data;
      if (isFiscal && !isFinanceiro && !isFiscalEspecial && fiscalSetor) {
        // Buscar contratos do setor do fiscal
        const contratosDoSetor = contratos.filter(contrato => contrato.setor === fiscalSetor);
        const numerosContratosDoSetor = contratosDoSetor.map(c => c.numeroContrato);
        
        // Filtrar notas que pertencem aos contratos do setor
        notasFiltradas = data.filter(nota => 
          numerosContratosDoSetor.includes(nota.numero_contrato)
        );
        
        console.log(`🔍 Filtrando todas as notas por setor: ${fiscalSetor}`);
        console.log(`📋 Contratos do setor: ${numerosContratosDoSetor.length}`);
        console.log(`📋 Notas filtradas: ${notasFiltradas.length}`);
      } else if (isFinanceiro || isFiscalEspecial) {
        console.log(`💰 Financeiro/Fiscal Especial vê TODAS as notas: ${data.length}`);
      }
      
      // Adicionar informações do contrato para cada nota
      const notasComContrato = notasFiltradas.map(nota => {
        const contrato = contratos.find(c => c.numeroContrato === nota.numero_contrato);
        return {
          ...nota,
          empresa: contrato?.empresa || 'Empresa não encontrada',
          setor: contrato?.setor || 'Setor não encontrado'
        };
      });
      
      setNotasFiscais(notasComContrato);
    } catch (error) {
      console.error('Erro ao buscar todas as notas fiscais:', error);
      setNotasFiscais([]);
    } finally {
      setLoading(false);
    }
  };

  // Buscar todas as notas para o Relatório mensal
  const fetchNotasFiscaisRelatorio = useCallback(async () => {
    setLoadingRelatorio(true);
    try {
      let url = unidadeSelecionada
        ? `${BACKEND_URL}/api/notas-fiscais?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/notas-fiscais`;
      url = withSetorScope(url, scopeOpts);
      const res = await apiFetch(url);
      if (!res.ok) throw new Error('Erro ao buscar notas fiscais');
      const data = await res.json();
      let notasFiltradas = data;
      if (isFiscal && !isFinanceiro && !isFiscalEspecial && fiscalSetor) {
        const contratosDoSetor = contratos.filter((c) => c.setor === fiscalSetor);
        const numeros = contratosDoSetor.map((c) => c.numeroContrato);
        // reforço client-side caso o backend ainda não filtre
        notasFiltradas = data.filter((n) => numeros.includes(n.numero_contrato) || n.setor === fiscalSetor);
      }
      setNotasFiscaisRelatorio(notasFiltradas);
    } catch (err) {
      console.error('Erro ao buscar notas para relatório:', err);
      setNotasFiscaisRelatorio([]);
    } finally {
      setLoadingRelatorio(false);
    }
  }, [contratos, isFiscal, isFinanceiro, isFiscalEspecial, fiscalSetor, unidadeSelecionada, user, rbacRole]);

  // Download do template Excel para importação de notas fiscais
  const handleDownloadTemplateNotas = useCallback(async () => {
    try {
      const res = await apiFetch('/api/notas-fiscais/template-excel');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao gerar template');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template-importacao-notas-fiscais-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({ open: true, message: 'Template baixado com sucesso.', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.message || 'Erro ao baixar template.', severity: 'error' });
    }
  }, []);

  // Importação em lote de notas fiscais via Excel
  const handleImportExcelNotas = useCallback(async (file) => {
    setImportingNotasExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch('/api/notas-fiscais/import-excel', {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) {
        setSnackbar({ open: true, message: data.error || 'Erro na importação.', severity: 'error' });
        return;
      }
      setImportResultModal({ open: true, result: data });
      if (data.imported > 0) {
        fetchNotasFiscaisRelatorio();
        if (contratoSelecionado) {
          fetchNotasFiscais(contratoSelecionado.numeroContrato);
        }
      }
      setSnackbar({ open: true, message: `Importação concluída: ${data.imported || 0} importadas.`, severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.message || 'Erro na importação.', severity: 'error' });
    } finally {
      setImportingNotasExcel(false);
    }
  }, [contratoSelecionado, fetchNotasFiscaisRelatorio, fetchNotasFiscais]);

  // Buscar aditivo do contrato
  const buscarAditivo = async (numeroContrato) => {
    try {
      const res = await apiFetch(withUnidade(`/api/aditivos?numeroContrato=${encodeURIComponent(numeroContrato)}`, unidadeSelecionada));
      if (res.ok) {
        const aditivos = await res.json();
        if (aditivos.length > 0) {
          // Retornar o último aditivo
          const ultimoAditivo = aditivos.sort((a, b) => 
            parseInt(b.numeroAditivo) - parseInt(a.numeroAditivo)
          )[0];
          return ultimoAditivo.numeroAditivo + '°';
        }
      }
      return '-';
    } catch (error) {
      console.error('Erro ao buscar aditivo:', error);
      return '-';
    }
  };

  // Buscar data de assinatura original do contrato
  const buscarDataAssinaturaOriginal = async (numeroContrato) => {
    try {
      const res = await apiFetch(withUnidade('/api/prestadores', unidadeSelecionada));
      if (res.ok) {
        const contratos = await res.json();
        const contrato = contratos.find(c => c.numeroContrato === numeroContrato);
        if (contrato) {
          return contrato.assinatura;
        }
      }
      return '-';
    } catch (error) {
      console.error('Erro ao buscar data de assinatura:', error);
      return '-';
    }
  };

  // Carregar contratos ao montar o componente
  useEffect(() => {
    fetchContratos();
  }, [fetchContratos, unidadeSelecionada]);

  // Carregar notas para relatório ao entrar no menu Relatório mensal ou Processo de pagamento (para resumo por setor)
  useEffect(() => {
    if ((menuSelected === 'relatorio-mensal' || menuSelected === 'proc-pagamento') && contratos.length > 0) {
      fetchNotasFiscaisRelatorio();
    }
  }, [menuSelected, contratos.length, fetchNotasFiscaisRelatorio]);

  // Aplicar filtro quando mudar o status ou as notas
  useEffect(() => {
    setNotasFiscaisFiltradas(notasFiscais);
  }, [notasFiscais]);

  // Abrir modal de avaliação quando navegar com state.avaliarNotaId (ex: vindo de Atividades após Autorizar)
  const location = useLocation();
  const navigate = useNavigate();
  const fluxoPagamentoRef = useRef({});
  useEffect(() => {
    const notaId = location.state?.avaliarNotaId;
    if (!notaId) return;
    fluxoPagamentoRef.current = {
      pagamentoAssinaturaDocumentoId: location.state?.pagamentoAssinaturaDocumentoId || null,
      avaliacaoAssinaturaDocumentoId: location.state?.avaliacaoAssinaturaDocumentoId || null,
    };
    apiFetch(withUnidade(`/api/notas-fiscais/por-id/${notaId}`, unidadeSelecionada))
      .then((r) => (r.ok ? r.json() : null))
      .then((nota) => {
        if (nota) {
          const contrato = contratos.find((c) => c.numeroContrato === nota.numero_contrato);
          if (contrato) setContratoSelecionado(contrato);
          handleAbrirAvaliacao(nota);
        }
        navigate(location.pathname, { replace: true, state: {} });
      })
      .catch(() => {});
  }, [location.state?.avaliarNotaId, location.pathname, contratos, navigate, unidadeSelecionada]);

  // Abrir menu Atividades / Fluxo quando vier via deep-link
  useEffect(() => {
    const menu = location.state?.menu;
    if (menu === 'atividades-notas-fiscais' || menu === 'fluxo-notas-fiscais') {
      setMenuSelected(menu);
    }
  }, [location.state?.menu]);

  // Quando selecionar um contrato
  const handleContratoChange = async (numeroContrato) => {
    console.log('🔍 handleContratoChange chamado com:', numeroContrato);
    const contrato = contratos.find(c => c.numeroContrato === numeroContrato);
    console.log('📋 Contrato encontrado:', contrato);
    
    if (contrato) {
      const aditivo = await buscarAditivo(numeroContrato);
      const dataAssinaturaOriginal = await buscarDataAssinaturaOriginal(numeroContrato);
      setContratoSelecionado({ 
        ...contrato, 
        aditivo,
        assinaturaOriginal: dataAssinaturaOriginal 
      });
      
      // Preencher automaticamente o valor bruto se não for eventual
      const isEventual = contrato.eventual === 1 || contrato.eventual === true;
      console.log('💰 isEventual:', isEventual, 'valorMes:', contrato.valorMes);
      
      if (!isEventual && contrato.valorMes) {
        const valorNumerico = parseFloat(contrato.valorMes.toString().replace(/[^\d.,]/g, '').replace(',', '.'));
        const valorFormatado = valorNumerico.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        console.log('✅ Preenchendo valor bruto:', valorFormatado);
        setNotaForm(prev => ({
          ...prev,
          valorBruto: valorFormatado,
          eventual: isEventual
        }));
      } else {
        console.log('⚠️ Contrato eventual ou sem valor - limpando campos');
        // Se for eventual, limpar os campos para preenchimento manual
        setNotaForm(prev => ({
          ...prev,
          valorBruto: '',
          valorLiquido: '',
          eventual: isEventual
        }));
      }
      
      fetchNotasFiscais(numeroContrato);
    } else {
      setContratoSelecionado(null);
      setNotasFiscais([]);
    }
  };

  // Abrir modal para adicionar nota
  const handleOpenModal = (nota = null) => {
    console.log('🚀 handleOpenModal chamado, nota:', nota);
    console.log('📊 contratoSelecionado:', contratoSelecionado);
    
    // Se não há contrato selecionado e não é edição, mostrar aviso
    if (!contratoSelecionado && !nota) {
      setSnackbar({ 
        open: true, 
        message: 'Selecione um contrato primeiro ou use os filtros para visualizar notas existentes', 
        severity: 'warning' 
      });
      return;
    }
    
    if (nota) {
      const anoCompetenciaEdit = nota.competencia && nota.competencia.includes('/')
        ? nota.competencia.split('/')[1]
        : String(new Date().getFullYear());
      setEditingNota(nota);
      setNotaForm({
        numeroNotaFiscal: nota.numero_nota_fiscal,
        dataEmissao: nota.data_emissao,
        dataRecebimento: nota.data_recebimento || '',
        dataEnvioContabilidade: nota.data_envio_contabilidade || '',
        dataRecebimentoContabilidade: nota.data_recebimento_contabilidade || '',
        dataVencimento: nota.data_vencimento || '',
        valorBruto: nota.valor_bruto ? nota.valor_bruto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
        valorLiquido: nota.valor_liquido ? nota.valor_liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
        competencia: nota.competencia || '',
        anoCompetencia: anoCompetenciaEdit,
        eventual: contratoSelecionado?.eventual || false,
        pagamentoEfetuado: nota.pagamento_efetuado === 1
      });
      // Definir PDF anexado se existir
      setPdfAnexado(nota.pdf_path ? {
        nome: nota.pdf_path.split('/').pop() || 'PDF anexado',
        url: `${BACKEND_URL}/api/notas-fiscais/${nota.id}/pdf`
      } : null);
    } else {
      // NOVO: Preservar valores do contrato selecionado ao adicionar nova nota
      const isEventual = contratoSelecionado?.eventual === 1 || contratoSelecionado?.eventual === true;
      let valorBrutoInicial = '';
      
      if (!isEventual && contratoSelecionado?.valorMes) {
        const valorNumerico = parseFloat(contratoSelecionado.valorMes.toString().replace(/[^\d.,]/g, '').replace(',', '.'));
        valorBrutoInicial = valorNumerico.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        console.log('💡 Preenchendo valor bruto no modal:', valorBrutoInicial);
      }
      
      setEditingNota(null);
      setNotaForm({
        numeroNotaFiscal: '',
        dataEmissao: '',
        dataRecebimento: '',
        dataEnvioContabilidade: '',
        dataRecebimentoContabilidade: '',
        dataVencimento: '',
        valorBruto: valorBrutoInicial,
        valorLiquido: '',
        competencia: '',
        anoCompetencia: String(new Date().getFullYear()),
        eventual: isEventual,
        pagamentoEfetuado: false
      });
      setPdfAnexado(null);
    }
    setPdfFile(null);
    setFormErrors({});
    setModalOpen(true);
  };

  const validarFormularioNota = () => {
    const errors = {};
    if (!contratoSelecionado) {
      return { errors, mensagem: 'Selecione um contrato antes de salvar a nota fiscal.' };
    }
    if (!String(notaForm.numeroNotaFiscal || '').trim()) {
      errors.numeroNotaFiscal = 'Informe o número da nota fiscal';
    }
    if (!notaForm.dataEmissao) {
      errors.dataEmissao = 'Informe a data de emissão';
    }
    if (!notaForm.competencia) {
      errors.competencia = 'Selecione o mês da competência';
    }
    if (!notaForm.anoCompetencia) {
      errors.anoCompetencia = 'Selecione o ano da competência';
    }

    const camposFaltando = [];
    if (errors.numeroNotaFiscal) camposFaltando.push('Nº Nota Fiscal');
    if (errors.dataEmissao) camposFaltando.push('Data de Emissão');
    if (errors.competencia || errors.anoCompetencia) camposFaltando.push('Competência');

    const mensagem = camposFaltando.length
      ? `Preencha os campos obrigatórios: ${camposFaltando.join(', ')}.`
      : '';

    return { errors, mensagem };
  };

  // Salvar nota fiscal
  const handleSalvarNota = async () => {
    const { errors, mensagem } = validarFormularioNota();
    if (Object.keys(errors).length > 0 || mensagem) {
      setFormErrors(errors);
      setSnackbar({
        open: true,
        message: mensagem || 'Preencha os campos obrigatórios.',
        severity: 'error'
      });
      return;
    }

    setFormErrors({});
    setUploading(true);

    try {
      const token = getToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      // Converter valores formatados para número
      const valorBrutoNumero = notaForm.valorBruto ? parseFloat(notaForm.valorBruto.replace(/\./g, '').replace(',', '.')) : null;
      const valorLiquidoNumero = notaForm.valorLiquido ? parseFloat(notaForm.valorLiquido.replace(/\./g, '').replace(',', '.')) : null;
      
      console.log('Dados do formulário:', notaForm);
      console.log('Valores convertidos:', { valorBrutoNumero, valorLiquidoNumero });

      // Se há um PDF para upload, usar FormData
      if (pdfFile) {
        const formData = new FormData();
        formData.append('numeroContrato', contratoSelecionado.numeroContrato);
        formData.append('empresa', contratoSelecionado.empresa);
        formData.append('numeroNotaFiscal', notaForm.numeroNotaFiscal);
        formData.append('dataEmissao', notaForm.dataEmissao);
        formData.append('dataRecebimento', notaForm.dataRecebimento || '');
        formData.append('dataEnvioContabilidade', notaForm.dataEnvioContabilidade || '');
        formData.append('dataRecebimentoContabilidade', notaForm.dataRecebimentoContabilidade || '');
        formData.append('dataVencimento', notaForm.dataVencimento || '');
        formData.append('dataEnviadoFinanceiro', notaForm.dataEnviadoFinanceiro || '');
        formData.append('valorBruto', valorBrutoNumero || '');
        formData.append('valorLiquido', valorLiquidoNumero || '');
        formData.append('competencia', notaForm.competencia);
        formData.append('pagamentoEfetuado', notaForm.pagamentoEfetuado);
        formData.append('pdfFile', pdfFile);

        const url = editingNota 
          ? `${BACKEND_URL}/api/notas-fiscais/${editingNota.id}/pdf`
          : `${BACKEND_URL}/api/notas-fiscais/pdf`;

        const res = await apiFetch(url, {
          method: editingNota ? 'PUT' : 'POST',
          body: formData
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Erro ao salvar nota fiscal com PDF');
        }
      } else {
        // Sem PDF, usar JSON normal
        const payload = {
          numeroContrato: contratoSelecionado.numeroContrato,
          empresa: contratoSelecionado.empresa,
          numeroNotaFiscal: notaForm.numeroNotaFiscal,
          dataEmissao: notaForm.dataEmissao,
          dataRecebimento: notaForm.dataRecebimento || null,
          dataEnvioContabilidade: notaForm.dataEnvioContabilidade || null,
          dataRecebimentoContabilidade: notaForm.dataRecebimentoContabilidade || null,
          dataVencimento: notaForm.dataVencimento || null,
          dataEnviadoFinanceiro: notaForm.dataEnviadoFinanceiro || null,
          valorBruto: valorBrutoNumero,
          valorLiquido: valorLiquidoNumero,
          competencia: notaForm.competencia,
          pagamentoEfetuado: notaForm.pagamentoEfetuado
        };

        const url = editingNota 
          ? `${BACKEND_URL}/api/notas-fiscais/${editingNota.id}`
          : `${BACKEND_URL}/api/notas-fiscais`;
        
        const method = editingNota ? 'PUT' : 'POST';

        const res = await apiFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const errorText = await res.text();
          console.error('Erro na resposta:', res.status, errorText);
          let msg = `Erro ao salvar nota fiscal: ${res.status}`;
          try {
            const parsed = JSON.parse(errorText);
            if (parsed?.error) msg = parsed.error;
          } catch (_) {
            if (errorText) msg = `${msg} - ${errorText}`;
          }
          throw new Error(msg);
        }
      }

      setSnackbar({ 
        open: true, 
        message: editingNota ? 'Nota fiscal atualizada com sucesso!' : 'Nota fiscal adicionada com sucesso!', 
        severity: 'success' 
      });

      setModalOpen(false);
      setPdfFile(null);
      fetchNotasFiscais(contratoSelecionado.numeroContrato);
    } catch (error) {
      console.error('Erro ao salvar nota:', error);
      setSnackbar({ 
        open: true, 
        message: `Erro ao salvar nota fiscal: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setUploading(false);
    }
  };

  // Cancelar/excluir nota fiscal (soft delete — somente setor de Contratos)
  const handleDeletarNota = async (notaId) => {
    if (!podeCancelarProcessoNf) {
      setSnackbar({
        open: true,
        message: 'Apenas o setor de Contratos pode cancelar processos/notas fiscais.',
        severity: 'error',
      });
      return false;
    }
    if (!window.confirm('Cancelar este processo/nota fiscal? Ele sairá das listas ativas (soft delete).')) {
      return false;
    }

    try {
      const res = await apiFetch(withUnidade(`/api/notas-fiscais/${notaId}`, unidadeSelecionada), {
        method: 'DELETE'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao cancelar nota fiscal');

      setSnackbar({
        open: true,
        message: data.message || 'Processo cancelado com sucesso!',
        severity: 'success',
      });
      if (contratoSelecionado?.numeroContrato) {
        fetchNotasFiscais(contratoSelecionado.numeroContrato);
      }
      fetchNotasFiscaisRelatorio();
      return true;
    } catch (error) {
      console.error('Erro ao deletar nota:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao cancelar nota fiscal',
        severity: 'error',
      });
      return false;
    }
  };


  // Lidar com seleção de arquivo PDF
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        // Se estamos editando e já existe um PDF, perguntar se quer substituir
        if (editingNota && editingNota.pdf_path) {
          const confirmReplace = window.confirm('Já existe um PDF anexado. Deseja substituir pelo novo arquivo?');
          if (!confirmReplace) {
            event.target.value = ''; // Limpar o input
            return;
          }
        }
        setPdfFile(file);
        setSnackbar({ open: true, message: 'PDF selecionado com sucesso!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Por favor, selecione apenas arquivos PDF', severity: 'error' });
      }
    }
  };

  // Remover PDF selecionado
  const handleRemovePdf = () => {
    setPdfFile(null);
    setSnackbar({ open: true, message: 'PDF removido', severity: 'info' });
  };

  const extrairNumeroDoNomePdf = (nome) => {
    const fonte = String(nome || '');
    const patterns = [
      /\bNF\s*[.\s-]*(\d+)/i,
      /\bNFS-?e\s*[.\s-]*(\d+)/i,
      /\bnota\s*fiscal\s*[.\s-]*(\d+)/i
    ];
    for (const pattern of patterns) {
      const match = fonte.match(pattern);
      if (match?.[1]) return match[1];
    }
    return '';
  };

  const aplicarDadosExtraidosPdf = (data, fileName = '') => {
    const hoje = new Date().toISOString().split('T')[0];
    const anoFromCompetencia =
      data.anoCompetencia ||
      (data.competencia && String(data.competencia).includes('/')
        ? String(data.competencia).split('/')[1]
        : '');

    const numeroDoArquivo = extrairNumeroDoNomePdf(fileName);

    setNotaForm((prev) => ({
      ...prev,
      numeroNotaFiscal: data.numeroNotaFiscal || numeroDoArquivo || prev.numeroNotaFiscal,
      dataEmissao: data.dataEmissao || prev.dataEmissao,
      dataVencimento: data.dataVencimento || prev.dataVencimento,
      valorBruto: data.valorBruto || prev.valorBruto,
      valorLiquido: data.valorLiquido || prev.valorLiquido,
      competencia: data.competencia || prev.competencia,
      anoCompetencia: anoFromCompetencia || prev.anoCompetencia,
      dataRecebimento: hoje
    }));

    setFormErrors((prev) => ({
      ...prev,
      numeroNotaFiscal: data.numeroNotaFiscal || numeroDoArquivo ? undefined : prev.numeroNotaFiscal,
      dataEmissao: data.dataEmissao ? undefined : prev.dataEmissao,
      competencia: data.competencia ? undefined : prev.competencia,
      anoCompetencia: anoFromCompetencia ? undefined : prev.anoCompetencia
    }));
  };

  const handleIntelligentPdfUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setSnackbar({ open: true, message: 'Por favor, selecione apenas arquivos PDF', severity: 'error' });
      return;
    }

    setPdfFile(file);
    setUploading(true);

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('pdfFile', file);

      const res = await apiFetch(withUnidade('/api/notas-fiscais/analisar-pdf', unidadeSelecionada), {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok && !data.numeroNotaFiscal && !data.competencia && !data.dataEmissao) {
        throw new Error(data.error || 'Erro ao analisar PDF da NFS-e');
      }

      aplicarDadosExtraidosPdf(data, file.name);

      const extraido = Boolean(
        data.numeroNotaFiscal ||
          data.dataEmissao ||
          data.competencia ||
          data.valorBruto ||
          data.valorLiquido
      );

      setSnackbar({
        open: true,
        message: data.aviso
          ? data.aviso
          : extraido
            ? 'Dados extraídos do PDF. Confira os campos antes de salvar.'
            : (data.error ||
                'PDF anexado, mas os dados não foram identificados. Preencha os campos manualmente.'),
        severity: data.aviso ? 'warning' : extraido ? 'success' : 'warning'
      });
    } catch (error) {
      console.error('Erro no Smart Upload:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Falha ao ler o PDF. Preencha manualmente.',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  // Abrir modal de documentos
  const handleAbrirDocumentos = (nota) => {
    setNotaSelecionada(nota);
    setDocumentosModalOpen(true);
    fetchDocumentos(nota.id);
  };

  // Abrir detalhe do processo (notas + certidões + PDF consolidado)
  const handleAbrirProcesso = async (nota) => {
    const numeroProcesso = nota?.numero_processo;
    if (!numeroProcesso) {
      setSnackbar({ open: true, message: 'Esta nota não possui número de processo.', severity: 'warning' });
      return;
    }
    setProcessoSelecionado(numeroProcesso);
    setProcessoDetalheOpen(true);
    setLoadingProcessoDetalhe(true);
    setNotasDoProcesso([]);
    setCertidoesProcesso([]);
    setAtividadeProcessoStatus(null);
    try {
      const url = withUnidade(`/api/notas-fiscais/documentos-processo?numero_processo=${encodeURIComponent(numeroProcesso)}`, unidadeSelecionada);
      const res = await apiFetch(url);
      if (!res.ok) throw new Error('Erro ao carregar dados do processo');
      const data = await res.json();
      const notasApi = data.notas || [];
      setNotasDoProcesso(notasApi.length > 0 ? notasApi : [{ nota_fiscal_id: nota.id, numero_nota_fiscal: nota.numero_nota_fiscal, competencia: nota.competencia, empresa: nota.empresa, valor_liquido: nota.valor_liquido }]);
      setCertidoesProcesso(data.certidoes || []);
      setAtividadeProcessoStatus(data.atividade_processo_status || null);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.message || 'Erro ao carregar processo', severity: 'error' });
      setNotasDoProcesso([{ nota_fiscal_id: nota.id, numero_nota_fiscal: nota.numero_nota_fiscal, competencia: nota.competencia, empresa: nota.empresa, valor_liquido: nota.valor_liquido }]);
    } finally {
      setLoadingProcessoDetalhe(false);
    }
  };

  const handleSalvarVencimentoCertidao = async (notaId, docId) => {
    try {
      const res = await apiFetch(withUnidade(`/api/notas-fiscais/${notaId}/documentos/${docId}`, unidadeSelecionada), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data_vencimento: certidaoVencimentoValor || null })
      });
      if (!res.ok) throw new Error('Erro ao salvar data');
      setCertidoesProcesso((prev) =>
        prev.map((c) => (c.id === docId ? { ...c, data_vencimento: certidaoVencimentoValor || null } : c))
      );
      setEditandoCertidaoVencimento(null);
      setCertidaoVencimentoValor('');
      setSnackbar({ open: true, message: 'Data de vencimento atualizada.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Erro ao salvar', severity: 'error' });
    }
  };

  // Preview do PDF consolidado via URL autenticada (evita carregar 30MB+ em blob na memória)
  useEffect(() => {
    const primeiraNotaId =
      processoDetalheOpen && notasDoProcessoOrdenadas.length > 0
        ? notasDoProcessoOrdenadas[0].nota_fiscal_id
        : null;
    if (!primeiraNotaId || !processoDetalheOpen) {
      setPdfConsolidadoBlobUrl(null);
      setPdfConsolidadoErroAuth(false);
      setLoadingPdfConsolidado(false);
      return;
    }
    const token = getToken();
    if (!token) {
      setPdfConsolidadoBlobUrl(null);
      setPdfConsolidadoErroAuth(true);
      setLoadingPdfConsolidado(false);
      return;
    }
    setPdfConsolidadoErroAuth(false);
    setLoadingPdfConsolidado(true);
    const apiPath = withUnidade(
      `/api/notas-fiscais/${primeiraNotaId}/pdf-consolidado?inline=1`,
      unidadeSelecionada
    );
    // iframe carrega direto (stream); não usa blob URL
    setPdfConsolidadoBlobUrl(getAuthenticatedApiUrl(apiPath));
    setLoadingPdfConsolidado(false);
  }, [processoDetalheOpen, notasDoProcessoOrdenadas.length, notasDoProcessoOrdenadas[0]?.nota_fiscal_id, unidadeSelecionada]);

  // Enviar nota para o fiscal
  const handleEnviarParaFiscal = async (notaId) => {
    try {
      const response = await apiFetch(withUnidade(`/api/notas-fiscais/${notaId}/status`, unidadeSelecionada), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'pendente_avaliacao' })
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Nota enviada para o fiscal com sucesso!',
          severity: 'success'
        });
        fetchNotasFiscais(contratoSelecionado?.numeroContrato);
      } else {
        const error = await response.json();
        setSnackbar({
          open: true,
          message: error.error || 'Erro ao enviar para o fiscal',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar para o fiscal:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao enviar para o fiscal',
        severity: 'error'
      });
    }
  };

  // Enviar todo o processo para o Fiscal (cria atividade em Contrato > Atividades)
  const [enviandoProcessoFiscal, setEnviandoProcessoFiscal] = useState(false);
  const handleEnviarProcessoParaFiscal = async () => {
    if (!processoSelecionado) return;
    setEnviandoProcessoFiscal(true);
    try {
      const res = await apiFetch(withUnidade('/api/notas-fiscais/processo/enviar-fiscal', unidadeSelecionada), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_processo: processoSelecionado })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSnackbar({
          open: true,
          message: data.message || 'Processo enviado ao fiscal. Vá em "Atividades de Notas Fiscais" e clique em ATUALIZAR para ver o processo.',
          severity: 'success'
        });
        fetchNotasFiscais(contratoSelecionado?.numeroContrato);
        setProcessoDetalheOpen(false);
      } else {
        setSnackbar({ open: true, message: data.error || 'Erro ao enviar processo ao fiscal', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Erro ao enviar processo ao fiscal', severity: 'error' });
    } finally {
      setEnviandoProcessoFiscal(false);
    }
  };

  // Abrir modal de avaliação mensal
  const handleAbrirAvaliacao = (nota) => {
    setNotaParaAvaliar(nota);
    setModalAvaliacaoOpen(true);
    setAvaliacaoForm({
      criterio1: 3,
      criterio2: 3,
      criterio3: 3,
      criterio4: 3,
      criterio5: 3,
      criterio6: 3,
      criterio7: 3,
      criterio8: 3,
      pontosPositivos: '',
      pontosNegativos: '',
      dataAvaliacao: new Date().toISOString().split('T')[0]
    });
  };

  // Calcular nota final
  const calcularNotaFinal = () => {
    const criterios = [
      avaliacaoForm.criterio1, avaliacaoForm.criterio2, avaliacaoForm.criterio3, avaliacaoForm.criterio4,
      avaliacaoForm.criterio5, avaliacaoForm.criterio6, avaliacaoForm.criterio7, avaliacaoForm.criterio8
    ];
    const soma = criterios.reduce((acc, nota) => acc + nota, 0);
    return (soma / criterios.length).toFixed(1);
  };

  // Classificar nota
  const getClassificacaoNota = (nota) => {
    if (nota >= 4.1) return { nivel: 'Excelente', cor: 'success' };
    if (nota >= 3.1) return { nivel: 'Ótimo', cor: 'primary' };
    if (nota >= 2.1) return { nivel: 'Bom', cor: 'info' };
    if (nota >= 1.1) return { nivel: 'Regular', cor: 'warning' };
    return { nivel: 'Ruim', cor: 'error' };
  };

  // Salvar avaliação
  const handleSalvarAvaliacao = async () => {
    if (!notaParaAvaliar) return;
    
    try {
      setUploading(true);
      
      const payload = {
        notaFiscalId: notaParaAvaliar.id,
        fiscalId: user?.id || 1,
        fiscalNome: user?.nome || user?.email || 'Fiscal',
        empresa: notaParaAvaliar.empresa,
        setor: contratoSelecionado?.setor || 'Geral',
        notaAvaliacao: parseFloat(calcularNotaFinal()),
        comentario: `Pontos Positivos: ${avaliacaoForm.pontosPositivos}\n\nPontos Negativos: ${avaliacaoForm.pontosNegativos}`,
        criterio1: avaliacaoForm.criterio1,
        criterio2: avaliacaoForm.criterio2,
        criterio3: avaliacaoForm.criterio3,
        criterio4: avaliacaoForm.criterio4,
        criterio5: avaliacaoForm.criterio5,
        criterio6: avaliacaoForm.criterio6,
        criterio7: avaliacaoForm.criterio7,
        criterio8: avaliacaoForm.criterio8,
        dataAvaliacao: avaliacaoForm.dataAvaliacao,
        pontosPositivos: avaliacaoForm.pontosPositivos,
        pontosNegativos: avaliacaoForm.pontosNegativos,
        numeroNotaFiscal: notaParaAvaliar.numero_nota_fiscal,
        competencia: notaParaAvaliar.competencia,
        valorLiquido: notaParaAvaliar.valor_liquido,
        parcela: notaParaAvaliar.parcela
      };

      const response = await apiFetch(withUnidade('/api/avaliacoes-mensais', unidadeSelecionada), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const avalDocId = data.avaliacaoAssinaturaDocumentoId || fluxoPagamentoRef.current?.avaliacaoAssinaturaDocumentoId;
        const pagDocId = data.pagamentoAssinaturaDocumentoId || fluxoPagamentoRef.current?.pagamentoAssinaturaDocumentoId;
        setSnackbar({
          open: true,
          message: avalDocId
            ? 'Avaliação e atesto salvos. Assine digitalmente a avaliação e, em seguida, o processo de pagamento.'
            : 'Avaliação salva com sucesso! Status atualizado para "Aguardando Pagamento"',
          severity: 'success'
        });
        setModalAvaliacaoOpen(false);
        setNotaParaAvaliar(null);

        if (contratoSelecionado) {
          fetchNotasFiscais(contratoSelecionado.numeroContrato);
        } else {
          await fetchTodasNotasFiscais();
        }

        if (avalDocId) {
          navigate(`/assinaturas/documentos/${avalDocId}`, {
            state: { proximoAssinaturaDocumentoId: pagDocId || null },
          });
        } else if (pagDocId) {
          navigate(`/assinaturas/documentos/${pagDocId}`);
        }
      } else {
        const error = await response.json();
        setSnackbar({
          open: true,
          message: error.error || 'Erro ao salvar avaliação',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao salvar avaliação:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar avaliação',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  // Abrir modal de comprovante de pagamento
  const handleAbrirComprovante = (nota) => {
    setNotaParaComprovante(nota);
    setModalComprovanteOpen(true);
    setComprovanteFile(null);
  };

  // Salvar comprovante de pagamento
  const handleSalvarComprovante = async () => {
    if (!notaParaComprovante || !comprovanteFile) {
      setSnackbar({
        open: true,
        message: 'Selecione um arquivo PDF para o comprovante',
        severity: 'warning'
      });
      return;
    }
    
    try {
      setUploadingDocumento(true);
      
      const formData = new FormData();
      formData.append('documento', comprovanteFile);
      formData.append('tipoDocumento', 'Comprovante de Pagamento');
      formData.append('subtipoDocumento', '');
      
      const res = await apiFetch(withUnidade(`/api/notas-fiscais/${notaParaComprovante.id}/documentos`, unidadeSelecionada), {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        await apiFetch(withUnidade(`/api/notas-fiscais/${notaParaComprovante.id}/status`, unidadeSelecionada), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'pago' })
        });
        
        await apiFetch(withUnidade(`/api/notas-fiscais/${notaParaComprovante.id}`, unidadeSelecionada), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            numeroNotaFiscal: notaParaComprovante.numero_nota_fiscal,
            dataEmissao: notaParaComprovante.data_emissao,
            dataRecebimento: notaParaComprovante.data_recebimento,
            dataEnvioContabilidade: notaParaComprovante.data_envio_contabilidade,
            dataRecebimentoContabilidade: notaParaComprovante.data_recebimento_contabilidade,
            dataVencimento: notaParaComprovante.data_vencimento,
            dataEnviadoFinanceiro: notaParaComprovante.data_enviado_financeiro,
            valorBruto: notaParaComprovante.valor_bruto,
            valorLiquido: notaParaComprovante.valor_liquido,
            pagamentoEfetuado: true
          })
        });
        
        setSnackbar({
          open: true,
          message: 'Comprovante anexado com sucesso! Status atualizado para "Pago"',
          severity: 'success'
        });
        
        setModalComprovanteOpen(false);
        setNotaParaComprovante(null);
        setComprovanteFile(null);
        
        // Recarregar notas fiscais
        if (contratoSelecionado) {
          fetchNotasFiscais(contratoSelecionado.numeroContrato);
        } else {
          // Se estiver usando filtros, recarregar todas as notas
          await fetchTodasNotasFiscais();
        }
      } else {
        const error = await res.json();
        setSnackbar({
          open: true,
          message: error.error || 'Erro ao anexar comprovante',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao anexar comprovante:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao anexar comprovante',
        severity: 'error'
      });
    } finally {
      setUploadingDocumento(false);
    }
  };


  // Buscar documentos de uma nota fiscal
  const fetchDocumentos = async (notaId) => {
    try {
      const res = await apiFetch(withUnidade(`/api/notas-fiscais/${notaId}/documentos`, unidadeSelecionada));
      if (res.ok) {
        const data = await res.json();
        setDocumentos(data);
      }
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
    }
  };

  // Upload de documento
  const handleUploadDocumento = async (tipoDocumento, subtipoDocumento, file) => {
    if (!file || file.type !== 'application/pdf') {
      setSnackbar({ open: true, message: 'Por favor, selecione apenas arquivos PDF', severity: 'error' });
      return;
    }

    setUploadingDocumento(true);
    const formData = new FormData();
    formData.append('documento', file);
    formData.append('tipoDocumento', tipoDocumento);
    formData.append('subtipoDocumento', subtipoDocumento);

    try {
      const res = await apiFetch(withUnidade(`/api/notas-fiscais/${notaSelecionada.id}/documentos`, unidadeSelecionada), {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        setSnackbar({ open: true, message: 'Documento adicionado com sucesso!', severity: 'success' });
        fetchDocumentos(notaSelecionada.id);
      } else {
        throw new Error('Erro ao adicionar documento');
      }
    } catch (error) {
      console.error('Erro ao adicionar documento:', error);
      setSnackbar({ open: true, message: 'Erro ao adicionar documento', severity: 'error' });
    } finally {
      setUploadingDocumento(false);
    }
  };

  // Lidar com seleção de arquivo para documentos
  const handleFileSelectDocumento = (tipoDocumento, subtipoDocumento = null) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleUploadDocumento(tipoDocumento, subtipoDocumento, file);
      }
    };
    input.click();
  };

  // Deletar documento
  const handleDeletarDocumento = async (docId) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) {
      return;
    }

    try {
      const res = await apiFetch(withUnidade(`/api/notas-fiscais/${notaSelecionada.id}/documentos/${docId}`, unidadeSelecionada), {
        method: 'DELETE'
      });

      if (res.ok) {
        setSnackbar({ open: true, message: 'Documento removido com sucesso!', severity: 'success' });
        fetchDocumentos(notaSelecionada.id);
      } else {
        throw new Error('Erro ao remover documento');
      }
    } catch (error) {
      console.error('Erro ao remover documento:', error);
      setSnackbar({ open: true, message: 'Erro ao remover documento', severity: 'error' });
    }
  };

  // Enviar para financeiro → atalho ARCA (montar processo + e-mail fiscal)
  const handleEnviarFinanceiro = async (notaId) => {
    const nota =
      (Array.isArray(notasFiscais) && notasFiscais.find((n) => String(n.id) === String(notaId))) ||
      null;
    const numeroContrato =
      nota?.numero_contrato || contratoSelecionado?.numeroContrato || '';
    const qs = new URLSearchParams();
    if (notaId) qs.set('notaId', String(notaId));
    if (numeroContrato) qs.set('numeroContrato', String(numeroContrato));
    navigate(`/arca/pdf/processo-pagamento?${qs.toString()}`);
  };

  // Abrir PDF com token (evita erro "Não autorizado" ao abrir em nova aba)
  const openPdfComAuth = async (url) => {
    const token = getToken();
    try {
      const res = await apiFetch(url);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSnackbar({ open: true, message: data.error || 'Erro ao abrir PDF. Faça login novamente.', severity: 'error' });
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      setSnackbar({ open: true, message: 'Erro ao abrir PDF', severity: 'error' });
    }
  };

  // Baixar PDF consolidado
  const handleBaixarPDFConsolidado = (notaId) => {
    openPdfComAuth(`${BACKEND_URL}/api/notas-fiscais/${notaId}/pdf-consolidado`);
  };

  // Formatar data
  const rotuloNotaFiscal = (nota) => {
    const num = nota?.numero_nota_fiscal || '';
    if (!nota?.unidade_nota) return num || '-';
    return `Nota nº ${num} - ${nota.unidade_nota}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Corrigir problema de fuso horário - adicionar 1 dia se necessário
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <NotasFiscaisLayout
          selectedMenu={menuSelected}
          onMenuChange={setMenuSelected}
        >
      <Box>
        <VisaoRestritaBanner />
        {/* Cabeçalho - oculto no Dashboard (tem próprio layout) */}
        {menuSelected !== 'dashboard' && menuSelected !== 'atividades-notas-fiscais' && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#1976d2' }} />
            <Typography variant="h4" fontWeight={600} color="primary">
              Controle de Notas Fiscais
            </Typography>
          </Box>
        )}

        {/* Conteúdo conforme menu */}
        {menuSelected === 'proc-pagamento' && !contratoSelecionado && (
          <ProcDePagamentoView
            contratos={contratos}
            todasNotas={notasFiscaisRelatorio}
            loading={loading}
            onSelectContrato={(c) => handleContratoChange(c.numeroContrato)}
            onDownloadTemplate={handleDownloadTemplateNotas}
            onImportExcel={handleImportExcelNotas}
            importing={importingNotasExcel}
          />
        )}

        {menuSelected === 'proc-pagamento' && contratoSelecionado && (
          <>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setContratoSelecionado(null)} sx={{ textTransform: 'none' }}>
                ← Voltar aos contratos
              </Button>
            </Box>
      {/* Informações do Contrato Selecionado */}
        <>
          <Card sx={{ mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, rgba(25,118,210,0.05) 0%, rgba(66,165,245,0.05) 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600} color="primary">
                  Informações do Contrato
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    onClick={handleAbrirGraficoContrato}
                    sx={{ 
                      color: '#4caf50',
                      '&:hover': { 
                        backgroundColor: 'rgba(76, 175, 80, 0.1)' 
                      }
                    }}
                    title="Ver Gráfico de Valores Mensais"
                  >
                    <BarChartIcon />
                  </IconButton>
                  <IconButton 
                    onClick={handleEditarContrato}
                    sx={{ 
                      color: '#1976d2',
                      '&:hover': { 
                        backgroundColor: 'rgba(25, 118, 210, 0.1)' 
                      }
                    }}
                    title="Editar Informações do Contrato"
                  >
                    <SettingsIcon />
                  </IconButton>
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Contrato</Typography>
                  <Typography variant="body1" fontWeight={600}>{contratoSelecionado.numeroContrato}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Aditivo</Typography>
                  <Typography variant="body1" fontWeight={600}>-</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Data de Assinatura</Typography>
                  <Typography variant="body1" fontWeight={600}>{formatDate(contratoSelecionado.assinatura)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Vigência Início</Typography>
                  <Typography variant="body1" fontWeight={600}>{formatDate(contratoSelecionado.vigenciaInicio)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Vigência Final</Typography>
                  <Typography variant="body1" fontWeight={600}>{formatDate(contratoSelecionado.vigenciaFim)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Código do Fornecedor</Typography>
                  <Typography variant="body1" fontWeight={600}>{contratoSelecionado.codigoFornecedor || contratoSelecionado.cnpj || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Código do Serviço</Typography>
                  <Typography variant="body1" fontWeight={600}>{contratoSelecionado.codigoServico || contratoSelecionado.setor || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    onClick={handleAbrirDetalhes}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(25,118,210,0.06)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(25,118,210,0.12)' }
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">Detalhes</Typography>
                    <Typography variant="body1" fontWeight={600} color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <DescriptionIcon fontSize="small" /> Ver Detalhes
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Botão Adicionar Nota Fiscal - Apenas Admin */}
          {isAdmin && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal()}
                sx={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25,118,210,0.4)'
                  }
                }}
              >
                Adicionar Nota Fiscal
              </Button>
            </Box>
          )}

          {/* Resumo Financeiro - Atualiza conforme ano selecionado */}
          {notasFiscaisFiltradas.length > 0 && (
            <Card sx={{ mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, rgba(25,118,210,0.05) 0%, rgba(66,165,245,0.05) 100%)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600, mb: 3 }}>
                  📊 Resumo Financeiro {anoFiltroNotas !== 'todos' && `(${anoFiltroNotas})`}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: '#e3f2fd', border: '1px solid #2196f3', boxShadow: '0 2px 8px rgba(33,150,243,0.2)' }}>
                      <CardContent>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                          💰 Valor Bruto Total
                        </Typography>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                          R$ {notasParaExibir.reduce((sum, nota) => sum + (parseFloat(nota.valor_bruto) || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: '#e8f5e8', border: '1px solid #4caf50', boxShadow: '0 2px 8px rgba(76,175,80,0.2)' }}>
                      <CardContent>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                          💵 Valor Líquido Total
                        </Typography>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                          R$ {notasParaExibir.reduce((sum, nota) => sum + (parseFloat(nota.valor_liquido) || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ff9800', boxShadow: '0 2px 8px rgba(255,152,0,0.2)' }}>
                      <CardContent>
                        <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                          📈 Valor Médio
                        </Typography>
                        <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                          R$ {notasParaExibir.length > 0
                            ? (notasParaExibir.reduce((sum, nota) => sum + (parseFloat(nota.valor_liquido) || 0), 0) / notasParaExibir.length).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : '0,00'
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          por nota fiscal
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Tabela de Notas Fiscais - Box Expansível */}
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Box 
              sx={{ 
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                backgroundColor: 'rgba(25,118,210,0.05)',
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, cursor: 'pointer' }} onClick={() => setNotasExpanded(!notasExpanded)}>
                <Typography variant="h6" fontWeight={600} color="primary">
                  📋 Notas Fiscais cadastradas
                </Typography>
                <IconButton size="small">
                  {notasExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Box>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={anoFiltroNotas}
                  onChange={(e) => setAnoFiltroNotas(e.target.value)}
                  displayEmpty
                  sx={{ backgroundColor: 'white', borderRadius: 1 }}
                >
                  <MenuItem value="todos">Todos os anos</MenuItem>
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Collapse in={notasExpanded}>
              <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: 'rgba(25,118,210,0.1)' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Parcela</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Processo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Competência</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>N° Nota Fiscal</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Data Emissão</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Data Recebimento</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Envio Contabilidade</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Recebimento Contabilidade</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Enviado Financeiro</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Valor Bruto</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Valor Líquido</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Data Vencimento</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>PDF</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Pagamento</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notasParaExibir.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={15} align="center">
                          {anoFiltroNotas === 'todos' ? 'Nenhuma nota fiscal cadastrada' : `Nenhuma nota em ${anoFiltroNotas}`}
                        </TableCell>
                      </TableRow>
                    ) : (
                      notasParaExibir.map((nota) => (
                        <TableRow key={nota.id}>
                          <TableCell>
                            <Chip label={`Parcela ${nota.parcela}`} color="primary" size="small" />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: nota.numero_processo ? 'pointer' : 'default',
                                '&:hover': nota.numero_processo ? { bgcolor: 'rgba(25,118,210,0.06)', borderRadius: 1 } : {}
                              }}
                              onClick={() => nota.numero_processo && handleAbrirProcesso(nota)}
                            >
                              <Typography variant="body2" fontWeight={600} color="secondary">
                                {nota.numero_processo ? `${nota.numero_processo} - ${nota.empresa} - R$ ${nota.valor_liquido ? parseFloat(nota.valor_liquido).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}` : '-'}
                              </Typography>
                              {nota.numero_processo && (
                                <Tooltip title="Abrir processo">
                                  <OpenInNewIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={nota.competencia || '-'} color="info" size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{rotuloNotaFiscal(nota)}</TableCell>
                          <TableCell>{formatDate(nota.data_emissao)}</TableCell>
                          <TableCell>{formatDate(nota.data_recebimento)}</TableCell>
                          <TableCell>{formatDate(nota.data_envio_contabilidade)}</TableCell>
                          <TableCell>{formatDate(nota.data_recebimento_contabilidade)}</TableCell>
                          <TableCell>{formatDate(nota.data_enviado_financeiro)}</TableCell>
                          <TableCell>
                            {nota.valor_bruto ? `R$ ${parseFloat(nota.valor_bruto).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}` : '-'}
                          </TableCell>
                          <TableCell>
                            {nota.valor_liquido ? `R$ ${parseFloat(nota.valor_liquido).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}` : '-'}
                          </TableCell>
                          <TableCell>
                            {nota.data_vencimento ? formatDate(nota.data_vencimento) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {(nota.pdf_consolidado_path || nota.pdf_path || nota.data_enviado_financeiro) ? (
                              <Chip 
                                label="PDF" 
                                color="success" 
                                size="small"
                                clickable
                                onClick={() => {
                                  if (nota.numero_processo) {
                                    handleAbrirProcesso(nota);
                                  } else if (nota.pdf_consolidado_path || nota.data_enviado_financeiro) {
                                    openPdfComAuth(`${BACKEND_URL}/api/notas-fiscais/${nota.id}/pdf-consolidado?inline=1`);
                                  } else {
                                    openPdfComAuth(`${BACKEND_URL}/api/notas-fiscais/${nota.id}/pdf`);
                                  }
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ) : (
                              <Chip label="Sem PDF" color="default" size="small" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox 
                              checked={nota.pagamento_efetuado === 1} 
                              disabled
                              color="success"
                            />
                          </TableCell>
                          <TableCell>
                            {nota.numero_processo ? (
                              <Tooltip title="Abrir processo (editar, documentos, avaliação, excluir, enviar financeiro)">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<OpenInNewIcon />}
                                  onClick={() => handleAbrirProcesso(nota)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Abrir processo
                                </Button>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              </CardContent>
            </Collapse>
          </Card>
        </>
      )}

      {/* Modal Adicionar/Editar Nota Fiscal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white'
        }}>
          {editingNota ? 'Editar Nota Fiscal' : 'Adicionar Nota Fiscal'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Informações do contrato selecionado */}
          {contratoSelecionado && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(25,118,210,0.08)', borderRadius: 2, border: '1px solid rgba(25,118,210,0.2)' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Contrato selecionado</Typography>
              <Typography variant="body1" fontWeight={600}>
                {contratoSelecionado.numeroContrato} – {contratoSelecionado.empresa || 'Sem empresa'}
              </Typography>
              {contratoSelecionado.setor && (
                <Typography variant="body2" color="text.secondary">Setor: {contratoSelecionado.setor}</Typography>
              )}
            </Box>
          )}

          {!editingNota && (
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                mb: 3,
                textAlign: 'center',
                bgcolor: uploading ? 'rgba(25,118,210,0.04)' : 'transparent'
              }}
            >
              {uploading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                  <CircularProgress size={36} />
                  <Typography variant="body2" color="text.secondary">
                    Lendo os dados da nota fiscal...
                  </Typography>
                </Box>
              ) : (
                <>
                  <PictureAsPdfIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    Faça o upload da NFS-e para preenchimento automático
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Os campos abaixo serão preenchidos para conferência. Você pode editar manualmente a qualquer momento.
                  </Typography>
                  <Button variant="contained" component="label" startIcon={<UploadIcon />} sx={{ textTransform: 'none' }}>
                    Selecionar PDF da NFS-e
                    <input
                      type="file"
                      hidden
                      accept="application/pdf"
                      onChange={handleIntelligentPdfUpload}
                    />
                  </Button>
                  {pdfFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 1.5 }} color="primary">
                      PDF selecionado: {pdfFile.name}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          {Object.keys(formErrors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Corrija os campos destacados abaixo para salvar a nota fiscal.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="N° Nota Fiscal *"
                value={notaForm.numeroNotaFiscal}
                onChange={(e) => {
                  setNotaForm({ ...notaForm, numeroNotaFiscal: e.target.value });
                  if (formErrors.numeroNotaFiscal) {
                    setFormErrors((prev) => ({ ...prev, numeroNotaFiscal: undefined }));
                  }
                }}
                fullWidth
                required
                error={Boolean(formErrors.numeroNotaFiscal)}
                helperText={formErrors.numeroNotaFiscal || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Ano da Competência *"
                value={notaForm.anoCompetencia || ''}
                onChange={(e) => {
                  setNotaForm({
                    ...notaForm,
                    anoCompetencia: e.target.value,
                    competencia: ''
                  });
                  if (formErrors.anoCompetencia) {
                    setFormErrors((prev) => ({ ...prev, anoCompetencia: undefined }));
                  }
                }}
                fullWidth
                required
                error={Boolean(formErrors.anoCompetencia)}
                helperText={formErrors.anoCompetencia || ''}
                SelectProps={{ native: true }}
              >
                <option value="">Selecione o ano</option>
                {anosCompetenciaDisponiveis.map((ano) => (
                  <option key={ano} value={String(ano)}>{ano}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Mês da Competência *"
                value={notaForm.competencia}
                onChange={(e) => {
                  setNotaForm({ ...notaForm, competencia: e.target.value });
                  if (formErrors.competencia) {
                    setFormErrors((prev) => ({ ...prev, competencia: undefined }));
                  }
                }}
                fullWidth
                required
                disabled={!notaForm.anoCompetencia}
                error={Boolean(formErrors.competencia)}
                helperText={formErrors.competencia || ''}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Selecione o mês</option>
                {mesesCompetencia.map((mes) => (
                  <option key={mes} value={mes}>{mes}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Número do Processo"
                value={editingNota?.numero_processo || "Será gerado automaticamente"}
                fullWidth
                disabled
                helperText="Este número será gerado automaticamente ao salvar"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#666',
                    fontWeight: 500
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data de Emissão"
                type="date"
                value={notaForm.dataEmissao}
                onChange={(e) => {
                  setNotaForm({ ...notaForm, dataEmissao: e.target.value });
                  if (formErrors.dataEmissao) {
                    setFormErrors((prev) => ({ ...prev, dataEmissao: undefined }));
                  }
                }}
                fullWidth
                required
                error={Boolean(formErrors.dataEmissao)}
                helperText={formErrors.dataEmissao || ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data de Recebimento"
                type="date"
                value={notaForm.dataRecebimento}
                onChange={(e) => setNotaForm({ ...notaForm, dataRecebimento: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data de Vencimento"
                type="date"
                value={notaForm.dataVencimento}
                onChange={(e) => setNotaForm({ ...notaForm, dataVencimento: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data Envio Contabilidade"
                type="date"
                value={notaForm.dataEnvioContabilidade}
                onChange={(e) => setNotaForm({ ...notaForm, dataEnvioContabilidade: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {editingNota && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data Recebimento Contabilidade"
                  type="date"
                  value={notaForm.dataRecebimentoContabilidade}
                  onChange={(e) => setNotaForm({ ...notaForm, dataRecebimentoContabilidade: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Este campo só pode ser preenchido ao editar"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderColor: '#ff9800',
                      '&:hover fieldset': {
                        borderColor: '#f57c00',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff9800',
                      },
                    }
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valor Bruto (R$)"
                value={notaForm.valorBruto}
                onChange={(e) => {
                  const valor = e.target.value.replace(/\D/g, '');
                  const valorFormatado = valor ? (parseInt(valor) / 100).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) : '';
                  setNotaForm({ ...notaForm, valorBruto: valorFormatado });
                }}
                fullWidth
                placeholder="Ex: 1.000,00"
                InputProps={{
                  startAdornment: 'R$ '
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valor Líquido (R$)"
                value={notaForm.valorLiquido}
                onChange={(e) => {
                  const valor = e.target.value.replace(/\D/g, '');
                  const valorFormatado = valor ? (parseInt(valor) / 100).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) : '';
                  setNotaForm({ ...notaForm, valorLiquido: valorFormatado });
                }}
                fullWidth
                placeholder="Ex: 950,00"
                InputProps={{
                  startAdornment: 'R$ '
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: 2,
                p: 2,
                border: '2px solid',
                borderColor: notaForm.eventual ? '#ff9800' : '#4caf50',
                borderRadius: 2,
                backgroundColor: notaForm.eventual ? 'rgba(255,152,0,0.08)' : 'rgba(76,175,80,0.08)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: notaForm.eventual ? '#ff9800' : '#4caf50'
                  }} />
                  <Typography variant="body1" fontWeight={600} color={notaForm.eventual ? 'warning.main' : 'success.main'}>
                    {notaForm.eventual ? 'Contrato Eventual' : 'Contrato Fixo'}
                  </Typography>
                  {notaForm.eventual && (
                    <Chip 
                      label="EVENTUAL" 
                      size="small" 
                      color="warning" 
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  {notaForm.eventual 
                    ? '💡 Preencha os valores manualmente' 
                    : '✓ Valor bruto preenchido automaticamente'
                  }
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                border: '2px dashed #1976d2', 
                borderRadius: 2, 
                p: 2, 
                textAlign: 'center',
                backgroundColor: (pdfFile || pdfAnexado) ? 'rgba(25,118,210,0.1)' : 'transparent'
              }}>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="pdf-upload"
                />
                <label htmlFor="pdf-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    sx={{ 
                      mb: 1,
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: 'rgba(25,118,210,0.1)'
                      }
                    }}
                  >
                    {pdfFile ? 'Alterar PDF' : (pdfAnexado ? 'Alterar PDF' : 'Anexar PDF da Nota Fiscal')}
                  </Button>
                </label>
                
                {/* Mostrar PDF anexado quando editando */}
                {pdfAnexado && !pdfFile && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <AttachFileIcon fontSize="small" />
                      PDF anexado: {pdfAnexado.nome}
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => openPdfComAuth(pdfAnexado.url)}
                      sx={{ mr: 1 }}
                    >
                      Visualizar PDF
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setPdfAnexado(null)}
                    >
                      Remover PDF
                    </Button>
                  </Box>
                )}

                {/* Mostrar novo PDF selecionado */}
                {pdfFile && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <AttachFileIcon fontSize="small" />
                      {pdfFile.name}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={handleRemovePdf}
                      sx={{ mt: 1 }}
                    >
                      Remover PDF
                    </Button>
                  </Box>
                )}
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  Apenas arquivos PDF são aceitos
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button 
            onClick={handleSalvarNota} 
            variant="contained"
            disabled={uploading}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
            }}
          >
            {uploading ? 'Salvando...' : (editingNota ? 'Atualizar' : 'Salvar')}
          </Button>
        </DialogActions>
      </Dialog>
          </>
        )}

        {menuSelected === 'relatorio-mensal' && (
          <RelatorioMensalView
            notasFiscais={notasFiscaisRelatorio}
            contratos={contratos}
            loading={loadingRelatorio}
            canCancelarProcesso={podeCancelarProcessoNf}
            onCancelarProcesso={(nota) => handleDeletarNota(nota.id)}
            onRefresh={async () => {
              setNotasFiscaisRelatorio([]);
              await fetchNotasFiscaisRelatorio();
            }}
            onBaixarPDFConsolidado={handleBaixarPDFConsolidado}
            onExportarExcel={async (mes, ano) => {
              try {
                const params = new URLSearchParams({ mes, ano });
                if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
                const res = await apiFetch(`/api/notas-fiscais/export-excel?${params}`, {
                  cache: 'no-store'
                });
                if (!res.ok) {
                  const data = await res.json().catch(() => ({}));
                  setSnackbar({ open: true, message: data.error || 'Erro ao exportar.', severity: 'error' });
                  return;
                }
                const arrayBuffer = await res.arrayBuffer();
                const blob = new Blob([arrayBuffer], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `notas_fiscais_${mes}_${ano}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                setSnackbar({ open: true, message: 'Exportação concluída.', severity: 'success' });
              } catch (err) {
                setSnackbar({ open: true, message: err.message || 'Erro ao exportar Excel.', severity: 'error' });
              }
            }}
            onImprimirRelatorio={() => window.print()}
            unidadeSelecionada={unidadeSelecionada}
            BACKEND_URL={BACKEND_URL}
          />
        )}

        {menuSelected === 'dashboard' && <DashboardFinanceiroPage />}

        {menuSelected === 'atividades-notas-fiscais' && <ProcessoPagamentoPage />}

        {menuSelected === 'fluxo-notas-fiscais' && <NotasFiscaisFluxoView />}
      </Box>

      {/* Modal de Gerenciamento de Documentos */}
      <Dialog open={documentosModalOpen} onClose={() => setDocumentosModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white'
        }}>
          Gerenciar Documentos - Nota Fiscal {notaSelecionada?.numero_nota_fiscal}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Atesto */}
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Atesto
                    </Typography>
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleFileSelectDocumento('Atesto')}
                      disabled={uploadingDocumento}
                      title="Adicionar atesto de assinatura"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Atesto de assinatura para a nota fiscal
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Relatórios */}
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Relatórios
                    </Typography>
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleFileSelectDocumento('Relatório')}
                      disabled={uploadingDocumento}
                      title="Adicionar relatório"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Adicione relatórios relacionados à nota fiscal
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Certidões */}
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Certidões
                    </Typography>
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleFileSelectDocumento('Certidão')}
                      disabled={uploadingDocumento}
                      title="Adicionar certidão"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {tiposDocumentos.certidoes.map((certidao, index) => (
                      <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{certidao.tipo}</Typography>
                        <Box display="flex" gap={0.5}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleFileSelectDocumento('Certidão', certidao.tipo)}
                            disabled={uploadingDocumento}
                            title={`Adicionar ${certidao.tipo}`}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Guias de Pagamento */}
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                      <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Guias de Pagamento
                    </Typography>
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleFileSelectDocumento('Guia de Pagamento')}
                      disabled={uploadingDocumento}
                      title="Adicionar guia de pagamento"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {tiposDocumentos.guias.map((guia, index) => (
                      <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{guia.tipo} - {guia.subtipo}</Typography>
                        <Box display="flex" gap={0.5}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleFileSelectDocumento('Guia de Pagamento', `${guia.tipo} - ${guia.subtipo}`)}
                            disabled={uploadingDocumento}
                            title={`Adicionar ${guia.tipo} - ${guia.subtipo}`}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Lista de Documentos Atuais */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Documentos Anexados</Typography>
                {uploadingDocumento && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="primary">
                      Enviando documento...
                    </Typography>
                  </Box>
                )}
              </Box>
              {documentos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum documento anexado ainda
                </Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Subtipo</TableCell>
                        <TableCell>Arquivo</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentos.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell>{doc.tipo_documento}</TableCell>
                          <TableCell>{doc.subtipo_documento || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={doc.nome_arquivo} 
                              color="success" 
                              size="small"
                              clickable
                              onClick={() => openPdfComAuth(`${BACKEND_URL}/api/notas-fiscais/${notaSelecionada.id}/documentos/${doc.id}/view`)}
                            />
                          </TableCell>
                          <TableCell>{formatDate(doc.data_upload)}</TableCell>
                          <TableCell>
                            {!visaoFiscal && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeletarDocumento(doc.id)}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDocumentosModalOpen(false)} color="secondary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer Detalhe do Processo */}
      <Drawer
        anchor="right"
        open={processoDetalheOpen}
        onClose={() => setProcessoDetalheOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, maxWidth: '100%' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={700}>
                Processo {processoSelecionado || ''}
              </Typography>
              <IconButton onClick={() => setProcessoDetalheOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {loadingProcessoDetalhe ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {(isAdmin || isFinanceiro) && notasDoProcessoOrdenadas.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {atividadeProcessoStatus === 'AUTORIZADO' ? (
                      <Chip label="Concluído" color="success" sx={{ fontWeight: 600 }} />
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={enviandoProcessoFiscal ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                          onClick={handleEnviarProcessoParaFiscal}
                          disabled={enviandoProcessoFiscal}
                          sx={{ textTransform: 'none' }}
                        >
                          Enviar processo para o Fiscal
                        </Button>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          O processo aparecerá em Contrato &gt; Atividades para o fiscal autorizar ou negar.
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
                {/* Notas do processo com botões de ação por nota */}
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Notas do processo – Ações
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                  {notasDoProcessoOrdenadas.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nenhuma nota neste processo.</Typography>
                  ) : (
                    notasDoProcessoOrdenadas.map((n) => {
                      const notaCompleta = notasFiscaisFiltradas.find((nf) => nf.id === n.nota_fiscal_id);
                      return (
                        <Card key={n.nota_fiscal_id} variant="outlined" sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip label={`NF ${n.numero_nota_fiscal}`} size="small" color="primary" variant="outlined" />
                            <Typography variant="body2" color="text.secondary">{n.competencia || '-'}</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              R$ {n.valor_liquido != null ? parseFloat(n.valor_liquido).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            {canEditNota() && (
                              <Tooltip title="Editar nota fiscal">
                                <IconButton size="small" onClick={() => { setProcessoDetalheOpen(false); if (notaCompleta) handleOpenModal(notaCompleta); }} sx={{ color: '#1976d2' }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(isAdmin || isFinanceiro) && (
                              <Tooltip title="Gerenciar documentos">
                                <IconButton size="small" onClick={() => { setProcessoDetalheOpen(false); if (notaCompleta) { setNotaSelecionada(notaCompleta); setDocumentosModalOpen(true); fetchDocumentos(notaCompleta.id); } }} sx={{ color: '#ff9800' }}>
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {notaCompleta && canAddAvaliacao(notaCompleta.setor) && notaCompleta.status === 'pendente_avaliacao' && (
                              <Tooltip title="Avaliação de fiscal">
                                <IconButton size="small" onClick={() => { setProcessoDetalheOpen(false); handleAbrirAvaliacao(notaCompleta); }} sx={{ color: '#9c27b0' }}>
                                  <AssignmentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEnviarContabilidade() && (
                              <Tooltip title="Montar processo no ARCA (financeiro + e-mail fiscal)">
                                <IconButton size="small" onClick={() => handleEnviarFinanceiro(n.nota_fiscal_id)} sx={{ color: '#4caf50' }}>
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {podeCancelarProcessoNf && !visaoFiscal && (
                              <Tooltip title="Cancelar processo (setor Contratos)">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    const ok = await handleDeletarNota(n.nota_fiscal_id);
                                    if (ok) {
                                      setNotasDoProcesso((prev) =>
                                        prev.filter((x) => x.nota_fiscal_id !== n.nota_fiscal_id)
                                      );
                                    }
                                  }}
                                  sx={{ color: '#f44336' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Card>
                      );
                    })
                  )}
                </Box>

                {/* PDF consolidado */}
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  PDF consolidado dos documentos anexados
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Atesto, Nota Fiscal, Relatório e demais documentos para o fiscal analisar.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {notasDoProcessoOrdenadas.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {notasDoProcessoOrdenadas.map((n) => (
                        <Box key={n.nota_fiscal_id} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PictureAsPdfIcon />}
                          onClick={() => handleBaixarPDFConsolidado(n.nota_fiscal_id)}
                          sx={{ textTransform: 'none' }}
                        >
                          NF {n.numero_nota_fiscal} – {n.competencia || 'PDF'}
                        </Button>
                        {(n.status === 'pendente_pagamento' || n.status === 'pago') && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<AssessmentIcon />}
                            onClick={() => openPdfComAuth(`${BACKEND_URL}/api/avaliacoes-mensais/nota/${n.nota_fiscal_id}/pdf`)}
                            sx={{ textTransform: 'none' }}
                          >
                            Ver PDF da Avaliação
                          </Button>
                        )}
                        </Box>
                      ))}
                      <Box sx={{ mt: 1, height: 320, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {loadingPdfConsolidado && (
                          <CircularProgress size={40} sx={{ position: 'absolute' }} />
                        )}
                        {!getToken() || pdfConsolidadoErroAuth ? (
                          <Typography variant="body2" color="error">
                            {pdfConsolidadoErroAuth ? 'Sessão expirada ou não autorizado. Faça login novamente para visualizar o PDF.' : 'Faça login para visualizar o PDF.'}
                          </Typography>
                        ) : pdfConsolidadoBlobUrl ? (
                          <iframe
                            title="PDF consolidado"
                            src={pdfConsolidadoBlobUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            onError={() => setPdfConsolidadoBlobUrl(null)}
                          />
                        ) : !loadingPdfConsolidado ? (
                          <Typography variant="body2" color="text.secondary">
                            PDF não disponível ou ainda não gerado. Clique em &quot;NF …&quot; acima para baixar/gerar.
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma nota no processo ou PDF ainda não gerado.
                    </Typography>
                  )}
                </Box>

                {/* Histórico de vencimento das certidões */}
                <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Histórico das datas de vencimento das certidões
                </Typography>
                {certidoesProcesso.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma certidão cadastrada para este processo.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'rgba(25,118,210,0.08)' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Tipo / Subtipo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Nota / Competência</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Data vencimento</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Ação</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {certidoesProcesso.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell>
                              {c.tipo_documento}
                              {c.subtipo_documento ? ` – ${c.subtipo_documento}` : ''}
                            </TableCell>
                            <TableCell>
                              NF {c.numero_nota_fiscal} {c.competencia ? ` · ${c.competencia}` : ''}
                            </TableCell>
                            <TableCell>
                              {editandoCertidaoVencimento === c.id ? (
                                <TextField
                                  type="date"
                                  size="small"
                                  value={certidaoVencimentoValor}
                                  onChange={(e) => setCertidaoVencimentoValor(e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 160 }}
                                />
                              ) : (
                                <Typography variant="body2" onClick={() => { setEditandoCertidaoVencimento(c.id); setCertidaoVencimentoValor(c.data_vencimento || ''); }}>
                                  {c.data_vencimento ? formatDate(c.data_vencimento) : '—'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {editandoCertidaoVencimento === c.id ? (
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                  <Button size="small" onClick={() => handleSalvarVencimentoCertidao(c.nota_fiscal_id, c.id)}>Salvar</Button>
                                  <Button size="small" onClick={() => { setEditandoCertidaoVencimento(null); setCertidaoVencimentoValor(''); }}>Cancelar</Button>
                                </Box>
                              ) : (
                                <Button size="small" startIcon={<AssignmentTurnedInIcon />} onClick={() => { setEditandoCertidaoVencimento(c.id); setCertidaoVencimentoValor(c.data_vencimento || ''); }}>
                                  {c.data_vencimento ? 'Alterar' : 'Informar'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Modal de Comprovante de Pagamento */}
      <Dialog open={modalComprovanteOpen} onClose={() => setModalComprovanteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon />
            <Typography variant="h6">
              Anexar Comprovante de Pagamento
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {notaParaComprovante && (
            <Box>
              {/* Informações da Nota */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#e0f7fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Nota Fiscal: <strong>{notaParaComprovante.numero_nota_fiscal}</strong>
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Empresa: <strong>{notaParaComprovante.empresa}</strong>
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Valor Líquido: <strong>R$ {notaParaComprovante.valor_liquido ? parseFloat(notaParaComprovante.valor_liquido).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}</strong>
                </Typography>
              </Box>

              {/* Upload de Arquivo */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Selecione o comprovante de pagamento (PDF)
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setComprovanteFile(e.target.files[0])}
                    style={{ display: 'none' }}
                    id="comprovante-upload"
                  />
                  <label htmlFor="comprovante-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<UploadIcon />}
                      sx={{ 
                        py: 2,
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        '&:hover': {
                          borderStyle: 'dashed',
                          borderWidth: 2
                        }
                      }}
                    >
                      {comprovanteFile ? comprovanteFile.name : 'Clique para selecionar o arquivo'}
                    </Button>
                  </label>
                </Box>

                {comprovanteFile && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1 }}>
                    <Typography variant="body2" color="success.main">
                      ✓ Arquivo selecionado: <strong>{comprovanteFile.name}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tamanho: {(comprovanteFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Informação importante */}
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>⚠️ Importante:</strong> Ao anexar o comprovante, a nota será automaticamente marcada como <strong>"Paga"</strong> e o checkbox de pagamento será marcado.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setModalComprovanteOpen(false)} 
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSalvarComprovante} 
            variant="contained"
            color="primary"
            disabled={uploadingDocumento || !comprovanteFile}
            startIcon={uploadingDocumento ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {uploadingDocumento ? 'Enviando...' : 'Anexar Comprovante'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Avaliação Mensal (moldura no estilo CI) */}
      <Dialog open={modalAvaliacaoOpen} onClose={() => setModalAvaliacaoOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{
          sx: {
            border: '3px solid',
            borderColor: 'primary.main',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(156, 39, 176, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
          color: 'white',
          borderRadius: 0
        }}>
          <Typography variant="h6" fontWeight={600}>
            Avaliação de Fornecedor Mensal de Nota Fiscal
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#fafafa' }}>
          {notaParaAvaliar && (
            <Paper variant="outlined" sx={{ p: 2, border: '2px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box>
              {/* Cabeçalho com dados da nota */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    📋 Informações da Nota Fiscal
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Empresa"
                        value={notaParaAvaliar.empresa}
                        fullWidth
                        margin="dense"
                        disabled
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="N° da Nota Fiscal"
                        value={notaParaAvaliar.numero_nota_fiscal}
                        fullWidth
                        margin="dense"
                        disabled
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Parcela"
                        value={notaParaAvaliar.parcela}
                        fullWidth
                        margin="dense"
                        disabled
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Valor Líquido"
                        value={`R$ ${notaParaAvaliar.valor_liquido ? parseFloat(notaParaAvaliar.valor_liquido).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '0,00'}`}
                        fullWidth
                        margin="dense"
                        disabled
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Critérios de Avaliação */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    ⭐ Critérios de Avaliação do Serviço/Produto
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Avalie cada critério de 1 (Ruim) a 5 (Excelente). A nota final será calculada automaticamente.
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {[
                      { key: 'criterio1', label: 'Serviço prestado conforme o Contrato' },
                      { key: 'criterio2', label: 'Serviços prestados de acordo com a rotina/programação de execução' },
                      { key: 'criterio3', label: 'Qualidade na entrega do serviço/produto' },
                      { key: 'criterio4', label: 'Comunicação acessível e ágil' },
                      { key: 'criterio5', label: 'Empenho na resolução de problemas sinalizados na prestação do serviço' },
                      { key: 'criterio6', label: 'Confiabilidade e Entrega' },
                      { key: 'criterio7', label: 'Segurança na Execução do Serviço' },
                      { key: 'criterio8', label: 'Alinhamento estratégico de acordo com as diretrizes do hospital' }
                    ].map((criterio, index) => (
                      <Grid item xs={12} key={criterio.key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ minWidth: '30px' }}>
                            {index + 1}.
                          </Typography>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {criterio.label}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: '120px' }}>
                            <InputLabel>Nota</InputLabel>
                            <Select
                              value={avaliacaoForm[criterio.key]}
                              onChange={(e) => setAvaliacaoForm({ ...avaliacaoForm, [criterio.key]: e.target.value })}
                              label="Nota"
                            >
                              <MenuItem value={1}>1 - Ruim</MenuItem>
                              <MenuItem value={2}>2 - Regular</MenuItem>
                              <MenuItem value={3}>3 - Bom</MenuItem>
                              <MenuItem value={4}>4 - Ótimo</MenuItem>
                              <MenuItem value={5}>5 - Excelente</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Nota Final Calculada */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      📊 Nota Final Calculada
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="primary">
                      {calcularNotaFinal()}
                    </Typography>
                    <Chip 
                      label={getClassificacaoNota(calcularNotaFinal()).nivel}
                      color={getClassificacaoNota(calcularNotaFinal()).cor}
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Média dos 8 critérios: ({[avaliacaoForm.criterio1, avaliacaoForm.criterio2, avaliacaoForm.criterio3, avaliacaoForm.criterio4, avaliacaoForm.criterio5, avaliacaoForm.criterio6, avaliacaoForm.criterio7, avaliacaoForm.criterio8].join(' + ')}) ÷ 8 = {calcularNotaFinal()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Campos adicionais */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data de Avaliação"
                    type="date"
                    value={avaliacaoForm.dataAvaliacao}
                    onChange={(e) => setAvaliacaoForm({ ...avaliacaoForm, dataAvaliacao: e.target.value })}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Pontos Positivos / Elogios"
                    value={avaliacaoForm.pontosPositivos}
                    onChange={(e) => setAvaliacaoForm({ ...avaliacaoForm, pontosPositivos: e.target.value })}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={3}
                    placeholder="Descreva os pontos positivos e elogios..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Pontos Negativos / Pontos para Melhorar"
                    value={avaliacaoForm.pontosNegativos}
                    onChange={(e) => setAvaliacaoForm({ ...avaliacaoForm, pontosNegativos: e.target.value })}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={3}
                    placeholder="Descreva os pontos negativos e sugestões de melhoria..."
                  />
                </Grid>
              </Grid>
            </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setModalAvaliacaoOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleSalvarAvaliacao} 
            variant="contained"
            disabled={uploading}
            sx={{
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              borderRadius: 2
            }}
          >
            {uploading ? <CircularProgress size={20} /> : 'Salvar Avaliação'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para editar informações do contrato */}
      <Dialog 
        open={modalEditarContratoOpen} 
        onClose={() => setModalEditarContratoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">Editar Informações do Contrato</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Código do Serviço"
                  value={contratoEditForm.codigoServico}
                  onChange={(e) => setContratoEditForm({
                    ...contratoEditForm,
                    codigoServico: e.target.value
                  })}
                  fullWidth
                  placeholder="Digite apenas números"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  helperText="Código para dar entrada em outro sistema"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Código do Fornecedor"
                  value={contratoEditForm.codigoFornecedor}
                  onChange={(e) => setContratoEditForm({
                    ...contratoEditForm,
                    codigoFornecedor: e.target.value
                  })}
                  fullWidth
                  placeholder="Digite apenas números"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  helperText="Código do cadastro no mesmo sistema do código de serviço"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalEditarContratoOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSalvarContrato}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              borderRadius: 2
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Detalhes do Contrato (Contrato + abas por aditivo) */}
      <Dialog
        open={modalDetalhesOpen}
        onClose={() => setModalDetalhesOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            <Typography variant="h6" fontWeight={600}>Detalhes do Contrato</Typography>
          </Box>
          <IconButton onClick={() => setModalDetalhesOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingDetalhes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Carregando detalhes...</Typography>
            </Box>
          ) : detalhesContrato ? (
            <>
              <Tabs value={tabDetalhes} onChange={(_, v) => setTabDetalhes(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                <Tab label="Contrato" />
                {detalhesContrato.aditivos.map((ad, idx) => (
                  <Tab key={ad.id} label={`${ad.numeroAditivo || idx + 1}º Aditivo`} />
                ))}
              </Tabs>
              <Box sx={{ p: 3 }}>
                {tabDetalhes === 0 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>Tipo de Contrato</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Chip label="Eventual" color={detalhesContrato.contrato.eventual ? 'primary' : 'default'} variant={detalhesContrato.contrato.eventual ? 'filled' : 'outlined'} size="small" />
                      <Chip label="Corporativo" color={detalhesContrato.contrato.corporativo ? 'primary' : 'default'} variant={detalhesContrato.contrato.corporativo ? 'filled' : 'outlined'} size="small" />
                      <Chip label="Serviço Médico" color={detalhesContrato.contrato.servicoMedico ? 'primary' : 'default'} variant={detalhesContrato.contrato.servicoMedico ? 'filled' : 'outlined'} size="small" />
                      <Chip label="Terceirizada" color={detalhesContrato.contrato.terceirizada ? 'primary' : 'default'} variant={detalhesContrato.contrato.terceirizada ? 'filled' : 'outlined'} size="small" />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Empresa</Typography><Typography fontWeight={600}>{detalhesContrato.contrato.empresa || '-'}</Typography></Grid>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Valor/mês</Typography><Typography fontWeight={600}>{detalhesContrato.contrato.valorMes ?? '-'}</Typography></Grid>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Assinatura</Typography><Typography fontWeight={600}>{formatDate(detalhesContrato.contrato.assinatura)}</Typography></Grid>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Vigência</Typography><Typography fontWeight={600}>{formatDate(detalhesContrato.contrato.vigenciaInicio)} – {formatDate(detalhesContrato.contrato.vigenciaFim)}</Typography></Grid>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Horas mensais</Typography><Typography fontWeight={600}>{detalhesContrato.contrato.horasMensais ?? '-'}</Typography></Grid>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Horas semanais</Typography><Typography fontWeight={600}>{detalhesContrato.contrato.horasSemanais ?? '-'}</Typography></Grid>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Quant. médicos</Typography><Typography fontWeight={600}>{detalhesContrato.contrato.quantidadeMedicos ?? '-'}</Typography></Grid>
                      <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Parcelas</Typography><Typography fontWeight={600}>{detalhesContrato.contrato.parcelas ?? '-'}</Typography></Grid>
                    </Grid>
                    {detalhesContrato.contrato.servicoMedico && detalhesContrato.contrato.vigenciaInicio && detalhesContrato.contrato.assinatura && detalhesContrato.contrato.parcelas && (() => {
                      const info = calcularParcelas(detalhesContrato.contrato.vigenciaInicio, detalhesContrato.contrato.assinatura, detalhesContrato.contrato.parcelas);
                      return info ? (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(25,118,210,0.08)', borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>Informações de Parcelas</Typography>
                          <Typography variant="body2">Data Assinatura: {info.dataAssinatura} | Vigência Início: {info.dataVigencia} | Primeira Parcela: {info.primeiraParcela} | Parcela Atual: {info.parcelaAtual} de {info.totalParcelas} | 60 meses: {info.sessentaMeses}</Typography>
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                )}
                {tabDetalhes > 0 && detalhesContrato.aditivos[tabDetalhes - 1] && (() => {
                  const ad = detalhesContrato.aditivos[tabDetalhes - 1];
                  return (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>{ad.numeroAditivo}º Aditivo – Informações alteradas</Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Assinatura</Typography><Typography fontWeight={600}>{formatDate(ad.assinatura)}</Typography></Grid>
                        <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Vigência</Typography><Typography fontWeight={600}>{formatDate(ad.vigenciaInicio)} – {formatDate(ad.vigenciaFim)}</Typography></Grid>
                        <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Valor/mês</Typography><Typography fontWeight={600}>{ad.valorMes ?? '-'}</Typography></Grid>
                        <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Parcelas</Typography><Typography fontWeight={600}>{ad.parcelas ?? '-'}</Typography></Grid>
                      </Grid>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, display: 'block' }}>Itens do Aditivo</Typography>
                      {ad.itens && ad.itens.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead><TableRow>
                              <TableCell><strong>Descrição</strong></TableCell>
                              <TableCell><strong>Unidade</strong></TableCell>
                              <TableCell align="right"><strong>Quantidade</strong></TableCell>
                              <TableCell align="right"><strong>Valor Unit.</strong></TableCell>
                              <TableCell align="right"><strong>Total</strong></TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                              {ad.itens.map((item, i) => (
                                <TableRow key={i}>
                                  <TableCell>{item.descricao || '-'}</TableCell>
                                  <TableCell>{item.unidade || '-'}</TableCell>
                                  <TableCell align="right">{item.quantidade ?? '-'}</TableCell>
                                  <TableCell align="right">{item.valor_unitario != null ? `R$ ${parseFloat(item.valor_unitario).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</TableCell>
                                  <TableCell align="right">{item.total != null ? `R$ ${parseFloat(item.total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>Nenhum item cadastrado para este aditivo.</Typography>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>Nenhum detalhe disponível</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalDetalhesOpen(false)} variant="outlined">Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      {/* Modal de Status das Notas */}
      <Dialog 
        open={modalStatusOpen} 
        onClose={() => setModalStatusOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <Typography variant="h6" fontWeight={600}>
            📋 Status das Notas Fiscais
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Filtros */}
          <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={statusFiltro === 'pendente_avaliacao' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => handleFiltroStatusModal('pendente_avaliacao')}
              startIcon={<WarningIcon />}
            >
              Aguardando Avaliação
            </Button>
            <Button
              variant={statusFiltro === 'pendente_pagamento' ? 'contained' : 'outlined'}
              color="info"
              onClick={() => handleFiltroStatusModal('pendente_pagamento')}
              startIcon={<PendingIcon />}
            >
              Aguardando Pagamento
            </Button>
            <Button
              variant={statusFiltro === 'pago' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => handleFiltroStatusModal('pago')}
              startIcon={<CheckCircleIcon />}
            >
              Finalizadas/Pagas
            </Button>
            <Button
              variant={statusFiltro === 'todas' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => handleFiltroStatusModal('todas')}
              startIcon={<ReceiptIcon />}
            >
              Todas
            </Button>
          </Box>

          {/* Lista de Notas */}
          {loadingStatus ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {notasStatus.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    Nenhuma nota encontrada para o filtro selecionado
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                  {notasStatus.map((nota, index) => (
                    <Card key={nota.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" color="primary" fontWeight={600}>
                            {nota.numero_processo || 'Sem processo'}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {nota.empresa}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Valor Bruto:</strong> R$ {nota.valor_bruto ? parseFloat(nota.valor_bruto).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Valor Líquido:</strong> R$ {nota.valor_liquido ? parseFloat(nota.valor_liquido).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {nota.pdf_path && (
                            <IconButton
                              size="small"
                              onClick={() => openPdfComAuth(`${BACKEND_URL}/api/notas-fiscais/${nota.id}/pdf`)}
                              sx={{ color: '#4caf50' }}
                              title="Ver PDF da Nota"
                            >
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleAbrirDocumentos(nota)}
                            sx={{ color: '#ff9800' }}
                            title="Gerenciar Documentos"
                          >
                            <DescriptionIcon fontSize="small" />
                          </IconButton>
                          {canAddAvaliacao(nota.setor) && nota.status === 'pendente_avaliacao' && (
                            <IconButton
                              size="small"
                              onClick={() => handleAbrirAvaliacao(nota)}
                              sx={{ color: '#9c27b0' }}
                              title="Avaliar Nota Fiscal"
                            >
                              <AssignmentIcon fontSize="small" />
                            </IconButton>
                          )}
                          {/* Autorizar/pagar: gestão financeira — Fiscal não vê (canMarcarComoPaga / canAuthorize) */}
                          {(canAuthorize?.() || canMarcarComoPaga()) && !visaoFiscal && nota.status === 'pendente_pagamento' && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleBaixarPDFConsolidado(nota.id)}
                                sx={{ color: '#9c27b0' }}
                                title="Download Documento Completo"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleAbrirComprovante(nota)}
                                sx={{ color: '#00bcd4' }}
                                title="Anexar Comprovante de Pagamento"
                              >
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setModalStatusOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Gráfico por Contrato */}
      <Dialog
        open={modalGraficoContrato}
        onClose={() => setModalGraficoContrato(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChartIcon color="primary" />
              <Typography variant="h6">
                Gráfico de Valores Mensais - {contratoSelecionado?.empresa || 'Contrato'}
              </Typography>
            </Box>
            <IconButton onClick={() => setModalGraficoContrato(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {loadingGraficoContrato ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Carregando dados do gráfico...
              </Typography>
            </Box>
          ) : (
            <Card elevation={3} sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
              <CardContent>
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pb: 1,
                  borderBottom: '1px solid #eee',
                  mb: 2
                }}>
                  <Typography variant="h6" component="h2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                    📊 Valores Brutos e Líquidos por Mês
                  </Typography>
                </Box>
                
                {dadosGraficoContrato && dadosGraficoContrato.length > 0 ? (
                  <Box sx={{ 
                    height: '450px', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    {/* Gráfico de barras verticais */}
                    <Box sx={{ 
                      height: '400px', 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      padding: '20px',
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      border: '2px solid #e0e0e0',
                      position: 'relative',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      {/* Linha de base (eixo X) */}
                      <Box sx={{
                        position: 'absolute',
                        bottom: '50px',
                        left: '20px',
                        right: '20px',
                        height: '4px',
                        backgroundColor: '#1976d2',
                        zIndex: 1,
                        borderRadius: '2px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                      
                      {/* Grade de fundo */}
                      <Box sx={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        right: '20px',
                        bottom: '50px',
                        backgroundImage: 'repeating-linear-gradient(to top, #f0f0f0 0px, #f0f0f0 1px, transparent 1px, transparent 25px)',
                        zIndex: 0
                      }} />
                      
                      {dadosGraficoContrato.map((item, index) => {
                        const valorBruto = parseFloat(item.valorBruto || 0);
                        const valorLiquido = parseFloat(item.valorLiquido || 0);
                        const maxValor = Math.max(...dadosGraficoContrato.map(d => Math.max(parseFloat(d.valorBruto || 0), parseFloat(d.valorLiquido || 0))));
                        const alturaBruto = maxValor > 0 ? (valorBruto / maxValor) * 300 : 0;
                        const alturaLiquido = maxValor > 0 ? (valorLiquido / maxValor) * 300 : 0;
                        
                        return (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            flex: 1,
                            height: '100%',
                            position: 'relative',
                            zIndex: 2
                          }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', width: '100%', height: '100%' }}>
                              {/* Barra Bruto */}
                              {valorBruto > 0 && (
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                                  <Box sx={{
                                    position: 'absolute',
                                    top: '-35px',
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    padding: '4px 6px',
                                    borderRadius: '6px',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    zIndex: 5,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                                  }}>
                                    R$ {valorBruto.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </Box>
                                  <Box sx={{ 
                                    width: '100%',
                                    height: `${alturaBruto}px`,
                                    backgroundColor: '#ff9800',
                                    borderRadius: '6px 6px 0 0',
                                    transition: 'all 0.6s ease',
                                    boxShadow: '0 4px 12px rgba(255,152,0,0.3)',
                                    border: '2px solid white',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 6px 20px rgba(255,152,0,0.5)',
                                      zIndex: 10
                                    }
                                  }} />
                                </Box>
                              )}
                              
                              {/* Barra Líquido */}
                              {valorLiquido > 0 && (
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                                  <Box sx={{
                                    position: 'absolute',
                                    top: '-60px',
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    padding: '4px 6px',
                                    borderRadius: '6px',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    zIndex: 5,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                                  }}>
                                    R$ {valorLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </Box>
                                  <Box sx={{ 
                                    width: '100%',
                                    height: `${alturaLiquido}px`,
                                    backgroundColor: '#4caf50',
                                    borderRadius: '6px 6px 0 0',
                                    transition: 'all 0.6s ease',
                                    boxShadow: '0 4px 12px rgba(76,175,80,0.3)',
                                    border: '2px solid white',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 6px 20px rgba(76,175,80,0.5)',
                                      zIndex: 10
                                    }
                                  }} />
                                </Box>
                              )}
                            </Box>
                            
                            {/* Nome do mês */}
                            <Typography variant="caption" sx={{ 
                              fontWeight: 'bold',
                              color: (valorBruto > 0 || valorLiquido > 0) ? '#333' : '#999',
                              textAlign: 'center',
                              fontSize: '11px',
                              marginTop: '8px'
                            }}>
                              {item.nome}
                            </Typography>
                            
                            {/* Indicador de notas */}
                            {item.totalNotas > 0 && (
                              <Typography variant="caption" sx={{ 
                                color: '#666',
                                fontSize: '9px',
                                textAlign: 'center'
                              }}>
                                {item.totalNotas} nota{item.totalNotas > 1 ? 's' : ''}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                    
                    {/* Legenda */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#ff9800', borderRadius: '4px' }} />
                        <Typography variant="body2">Valor Bruto</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#4caf50', borderRadius: '4px' }} />
                        <Typography variant="body2">Valor Líquido</Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '300px',
                    flexDirection: 'column'
                  }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      📊 Nenhum dado encontrado
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Não há dados de valores para este contrato
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalGraficoContrato(false)} variant="outlined">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal resultado da importação em lote */}
      <Dialog
        open={importResultModal.open}
        onClose={() => setImportResultModal({ open: false, result: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resultado da importação</DialogTitle>
        <DialogContent>
          {importResultModal.result && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Total processadas: <strong>{importResultModal.result.total ?? 0}</strong>
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={`Importadas: ${importResultModal.result.imported ?? 0}`}
                  color="success"
                  size="small"
                />
                {(importResultModal.result.termoCompromisso ?? 0) > 0 && (
                  <Chip
                    label={`Termo de Compromisso: ${importResultModal.result.termoCompromisso}`}
                    color="info"
                    size="small"
                  />
                )}
                <Chip
                  label={`Ignoradas (duplicadas): ${importResultModal.result.ignored ?? 0}`}
                  color="default"
                  size="small"
                />
                {(importResultModal.result.errors ?? 0) > 0 && (
                  <Chip
                    label={`Erros: ${importResultModal.result.errors}`}
                    color="error"
                    size="small"
                  />
                )}
              </Box>
              {Array.isArray(importResultModal.result.errorDetails) && importResultModal.result.errorDetails.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="error">Detalhes dos erros:</Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 0.5, maxHeight: 150, overflow: 'auto' }}>
                    {importResultModal.result.errorDetails.map((e, i) => (
                      <li key={i}>
                        <Typography variant="caption" color="text.secondary">
                          Linha {e.row}: {e.message || e}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
              {Array.isArray(importResultModal.result.ignoredDetails) && importResultModal.result.ignoredDetails.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Ignoradas (duplicadas):</Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 0.5, maxHeight: 100, overflow: 'auto' }}>
                    {importResultModal.result.ignoredDetails.slice(0, 10).map((d, i) => (
                      <li key={i}>
                        <Typography variant="caption" color="text.secondary">
                          {typeof d === 'string' ? d : `${d.numero_contrato || d.numeroContrato} / ${d.competencia} / NF ${d.numero_nota_fiscal ?? d.numeroNotaFiscal}`}
                        </Typography>
                      </li>
                    ))}
                    {(importResultModal.result.ignoredDetails?.length ?? 0) > 10 && (
                      <Typography variant="caption" color="text.secondary">
                        … e mais {importResultModal.result.ignoredDetails.length - 10}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResultModal({ open: false, result: null })} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </NotasFiscaisLayout>
  );
}

